import { z } from 'zod'
import { asEnumType, asField, asObjectType } from '@gqloom/zod'
import { GraphQLJSON } from 'graphql-scalars'

/**
 * Client Favorite Instructor schema
 */
export const clientFavoriteInstructorSchema = z
  .object({
    instructorId: z.string().uuid(),
  })
  .register(asObjectType, {
    name: 'ClientFavoriteInstructor',
    description: 'Client favorite instructor reference',
  })

export type ClientFavoriteInstructor = z.infer<typeof clientFavoriteInstructorSchema>
import type { FieldValues, Control } from 'react-hook-form'

import {
  ClientSiteLinkSource,
  ClientSiteStatus,
} from '@hapana/shared/constants/client-site.constants'
import {
  createPaginationWithCursorArgsSchema,
  createPaginationWithCursorResultSchema,
  orderByDirectionEnumSchema,
} from '../database'
import { sortDirectionEnumSchema } from '../common/pagination-schemas'
import { formExternalResultSourceSchema } from '../site-forms/form-external-result'
import {
  formCompletedViaSchema,
  formRequiredBeforeSchema,
} from '../site-forms/form-workflow-schemas'
import { CLIENT_SOURCE_VALUES } from './client-source'
import { staffUserSmartGroupPublicSchema } from './staff-user-smart-group-schemas'
import { clientFiltersSchema } from './client-filter-schemas'
import { clientFileSchema } from './client-file-schemas'
import { imageFileConfigFieldSchema } from '../file-upload/image.schemas'
import {
  PASSWORD_MIN_LENGTH,
  PASSWORD_MAX_LENGTH,
  PASSWORD_UPPERCASE_REGEX,
  PASSWORD_SPECIAL_CHARACTERS_REGEX,
} from '../../constants/validations'
// Re-export FilterOperator for backward compatibility
export { FilterOperator, type FilterOperatorType } from './client-filter-schemas'

// Client Search Result Schema (lightweight for kbar/search)
export const clientSearchResultSchema = z.object({
  id: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  name: z.string().nullable(),
  email: z.string().nullable(),
  mobilePhoneNumber: z.string().nullable(),
})

export type ClientSearchResult = z.infer<typeof clientSearchResultSchema>

// Client Filter Schemas
export const filterCategorySchema = z.enum(['schedule', 'contract', 'profile', 'paymentBilling'])
export type FilterCategoryType = z.infer<typeof filterCategorySchema>

export const filterTypeSchema = z.enum([
  'select',
  'multiselect',
  'date_range',
  'numeric_range',
  'operator_numeric',
  'operator_text',
  'operator_date',
  'operator_multiselect',
  'operator_select',
])
export type FilterType = z.infer<typeof filterTypeSchema>

export const filterOptionSchema = z.object({
  value: z.union([z.string(), z.number()]),
  label: z.string(),
})
export type FilterOption = z.infer<typeof filterOptionSchema>

export const filterOperatorSchema = z.object({
  value: z.string(),
  label: z.string(),
})
export type FilterOperatorOption = z.infer<typeof filterOperatorSchema>

export const filterDefinitionSchema = z.object({
  id: z.string(),
  category: filterCategorySchema,
  categoryLabel: z.string(),
  label: z.string().nullish(),
  name: z.string().nullish(),
  type: filterTypeSchema,
  operators: z.array(filterOperatorSchema).nullish(),
  options: z.array(filterOptionSchema).nullish(),
  placeholder: z.string().nullish(),
})
export type FilterDefinition = z.infer<typeof filterDefinitionSchema>

export const filterValueSchema = z.union([
  z.string(),
  z.array(z.string()),
  z.object({
    from: z.union([z.string(), z.date()]).nullish(),
    to: z.union([z.string(), z.date()]).nullish(),
    min: z.union([z.string(), z.number()]).nullish(),
    max: z.union([z.string(), z.number()]).nullish(),
  }),
])
export type FilterValue = z.infer<typeof filterValueSchema>

export const appliedFilterSchema = z.object({
  id: z.string(),
  type: z.string(),
  category: z.string(),
  name: z.string(),
  operator: z.string(),
  value: filterValueSchema,
  logicalOperator: z.string().nullish(),
  orderIndex: z.number().optional(),
  categoryLabel: z.string().optional(),
  filterLabel: z.string().optional(),
})
export type AppliedFilter = z.infer<typeof appliedFilterSchema>

export const schemaFilterConditionSchema = z.object({
  operator: z.string(),
  value: filterValueSchema.optional(),
  from: z.union([z.string(), z.date()]).optional(),
  to: z.union([z.string(), z.date()]).optional(),
  min: z.union([z.number(), z.string()]).optional(),
  max: z.union([z.number(), z.string()]).optional(),
})
export type SchemaFilterCondition = z.infer<typeof schemaFilterConditionSchema>

export const schemaFilterSchema = z.object({
  using: z.array(schemaFilterConditionSchema),
  logicalOperator: z.string(),
  orderIndex: z.number(),
})
export type SchemaFilter = z.infer<typeof schemaFilterSchema>

export const smartGroupFiltersSchema = z.record(z.string(), schemaFilterSchema)
export type SmartGroupFilters = z.infer<typeof smartGroupFiltersSchema>

export const defaultFilterValuesSchema = z.record(z.string(), z.any())
export type DefaultFilterValues = z.infer<typeof defaultFilterValuesSchema>

export const operatorValueSchema = z.object({
  operator: z.string().optional(),
  value: filterValueSchema.optional(),
})
export type OperatorValue = z.infer<typeof operatorValueSchema>

export const numericRangeValueSchema = z.object({
  min: z.union([z.string(), z.number()]).optional(),
  max: z.union([z.string(), z.number()]).optional(),
})
export type NumericRangeValue = z.infer<typeof numericRangeValueSchema>

export const dateRangeValueSchema = z.object({
  from: z.union([z.string(), z.date()]).optional(),
  to: z.union([z.string(), z.date()]).optional(),
})
export type DateRangeValue = z.infer<typeof dateRangeValueSchema>

export const filterDialogFormDataSchema = z.record(z.string(), z.any())
export type FilterDialogFormData = z.infer<typeof filterDialogFormDataSchema>

export const createAppliedFilterSchema = appliedFilterSchema.omit({
  categoryLabel: true,
  filterLabel: true,
})

export const updateAppliedFilterSchema = appliedFilterSchema.partial()

export const validateAppliedFilter = (data: unknown): AppliedFilter => {
  return appliedFilterSchema.parse(data)
}

export const validateFilterDefinition = (data: unknown): FilterDefinition => {
  return filterDefinitionSchema.parse(data)
}

export const validateSchemaFilter = (data: unknown): SchemaFilter => {
  return schemaFilterSchema.parse(data)
}

export const validateSmartGroupFilters = (data: unknown): SmartGroupFilters => {
  return smartGroupFiltersSchema.parse(data)
}

// Additional Filter Component Schemas
export const dropdownOptionSchema = z.object({
  value: z.union([z.string(), z.number()]),
  label: z.string(),
})
export type DropdownOption = z.infer<typeof dropdownOptionSchema>

export const filterWithOptionsSchema = filterDefinitionSchema.extend({
  options: z.array(dropdownOptionSchema).optional(),
})
export type FilterWithOptions = z.infer<typeof filterWithOptionsSchema>

export const filtersByCategorySchema = z.record(z.string(), z.array(filterDefinitionSchema))
export type FiltersByCategory = z.infer<typeof filtersByCategorySchema>

// Component Props Types (TypeScript interfaces since Zod doesn't support function validation)
export interface ClientsFilterDialogProps {
  onApplyFilters: (filters: AppliedFilter[]) => void
  currentFilters?: AppliedFilter[]
  onSmartGroupSaved?: () => void
  staffUserId?: string
  siteId?: string
  isFromAddEditSmartGroup?: boolean
}

export interface DetailViewProps {
  selectedFilter?: FilterWithOptions
  onBack: () => void
  onApplyFilter: () => void
  renderFilterDetail?: () => React.ReactNode
}

// Component Props Types (TypeScript interfaces since Zod doesn't support function validation)
export interface AppliedFiltersViewProps {
  appliedFilters: AppliedFilter[]
  onEditFilter: (filter: AppliedFilter, index: number) => void
  onRemoveFilter: (index: number) => void
  onSaveAsSmartGroup: (name: string, keepFilters: boolean) => void
  onViewResults: (keepFilters: boolean) => void
  isCreatingSmartGroup?: boolean
  filters: AppliedFilter[]
  rawFilters: AppliedFilter[]
  onAddFilter: () => void
  onDeleteFilter: (filterId: string) => void
}

export interface FilterViewProps {
  filtersByCategory: FiltersByCategory
  searchTerm: string
  onSearchChange: (term: string) => void
  onFilterSelect: (filter: FilterDefinition) => void
  onSearch: (term: string) => void
  onFilterClick: (filter: FilterDefinition) => void
  onApplyFilters: () => void
  renderFilterButton: (filter: FilterDefinition) => React.ReactNode
}

export interface OperatorFilterProps<T extends FieldValues = FieldValues> {
  selectedFilter?: FilterWithOptions
  control: Control<T>
}

export interface FormData extends FieldValues {
  [key: string]: any
}

// Client status enum - matches frontend status values
// Priority order: Deleted > Blocked > Prospect > Suspended > Membership > Package > Intro > Alumni > Lead
//
// OC-1492: Prospect is a marketplace contact we created as a client who has not engaged
// with the club yet — no Firebase auth user, so they cannot log in. It ranks above the
// service-derived statuses because it is a stored fact (Client.isProspect), not something
// the services imply: a prospect has no services, which is exactly what Lead already means.
export enum ClientStatus {
  Deleted = 'deleted',
  Blocked = 'blocked',
  Prospect = 'prospect',
  Suspended = 'suspended',
  Membership = 'membership',
  Package = 'package',
  Intro = 'intro',
  Pending = 'pending',
  Alumni = 'alumni',
  Aggregator = 'aggregator',
  Lead = 'lead',
}

export const clientStatusEnumSchema = z
  .enum(Object.values(ClientStatus) as [string, ...string[]])
  .register(asEnumType, {
    name: 'ClientStatus',
    description: 'Current status of a client',
  })

export const CLIENT_ACTIVE_STATUSES = [
  ClientStatus.Membership,
  ClientStatus.Package,
  ClientStatus.Intro,
  ClientStatus.Aggregator,
  ClientStatus.Suspended,
] as const

export const CLIENT_SLEEPING_ELIGIBLE_STATUSES = [
  ClientStatus.Membership,
  ClientStatus.Package,
  ClientStatus.Intro,
  ClientStatus.Aggregator,
] as const

export type ClientActiveStatus = (typeof CLIENT_ACTIVE_STATUSES)[number]
export type ClientSleepingEligibleStatus = (typeof CLIENT_SLEEPING_ELIGIBLE_STATUSES)[number]

// Schema for computed permissions (internal use, not exposed to GraphQL)
export const computedPermissionsSchema = z.object({
  corporate: z.string(),
  access: z.string(),
  sites: z.string(), // JSON string for internal storage
})

export type ComputedPermissions = z.infer<typeof computedPermissionsSchema>

// Sort direction enum
export enum ClientSortDirection {
  ASC = 'ASC',
  DESC = 'DESC',
}

export const clientSortDirectionEnumSchema = z
  .nativeEnum(ClientSortDirection)
  .register(asEnumType, {
    name: 'ClientSortDirection',
    description: 'Sort direction for client queries',
  })

// Address schema for JSON field (used internally, GLoom auto-generates GraphQL types)
export const clientAddressSchema = z.object({
  unit: z.string().optional(),
  streetNumber: z.string().optional(),
  streetAddress: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  zipCode: z.string().optional(),
  country: z.string().optional(),
})

// Emergency contact address schema for JSON field
export const clientEmergencyContactAddressSchema = z.object({
  unit: z.string().optional(),
  streetNumber: z.string().optional(),
  street: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  zipCode: z.string().optional(),
  country: z.string().optional(),
})

// Emergency contact schema for JSON field (used internally, GLoom auto-generates GraphQL types)
export const clientEmergencyContactSchema = z.object({
  name: z.string().optional(),
  phone: z.string().optional(),
  email: z.string().nullish(),
  address: clientEmergencyContactAddressSchema.nullable().optional(),
  relationship: z.string().optional(),
})

// ClientFamily relationship enum
export const clientFamilyRelationshipSchema = z
  .enum(['SPOUSE', 'PARTNER', 'CHILD', 'PARENT', 'SIBLING', 'FRIEND', 'OTHER'])
  .register(asEnumType, {
    name: 'ClientFamilyRelationship',
    description: 'Type of relationship in ClientFamily',
  })
export type ClientFamilyRelationship = z.infer<typeof clientFamilyRelationshipSchema>

// ClientFamily schema for input (create/update)
export const clientFamilySchema = z.object({
  name: z.string().min(1),
  email: z.string().email(),
  relationship: clientFamilyRelationshipSchema,
})
export type ClientFamilySchema = z.infer<typeof clientFamilySchema>

// ClientFamily record schema for database
export const clientFamilyRecordSchema = z.object({
  clientId: z.string().uuid(),
  familyMemberClientId: z.string().uuid(),
  linkedClientId: z.string().uuid().nullable(),
  name: z.string(),
  email: z.string().nullable(),
  relationship: clientFamilyRelationshipSchema,
  relationshipInverse: clientFamilyRelationshipSchema,
  isPrimaryAccountHolder: z.boolean(),
  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().nullable(),
})
export type ClientFamilyRecordSchema = z.infer<typeof clientFamilyRecordSchema>

// ClientFamily public schema (for GraphQL, excludes deletedAt)
export const clientFamilyPublicSchema = clientFamilyRecordSchema
  .omit({ deletedAt: true })
  .register(asObjectType, {
    name: 'ClientFamily',
    description: 'Client family relation record',
  })
export type ClientFamilyPublicSchema = z.infer<typeof clientFamilyPublicSchema>

// Create ClientFamily input schema
export const createClientFamilyInputSchema = clientFamilySchema.register(asObjectType, {
  name: 'CreateClientFamilyInput',
  description: 'Input for creating a client family relation',
})
export type CreateClientFamilyInputSchema = z.infer<typeof createClientFamilyInputSchema>

// Update ClientFamily input schema
export const updateClientFamilyInputSchema = clientFamilySchema.partial().register(asObjectType, {
  name: 'UpdateClientFamilyInput',
  description: 'Input for updating a client family relation',
})
export type UpdateClientFamilyInputSchema = z.infer<typeof updateClientFamilyInputSchema>

// ClientBookingBuddy status enum
export const clientBookingBuddyStatusSchema = z
  .enum(['PENDING', 'ACCEPTED', 'DECLINED', 'REVOKED'])
  .register(asEnumType, {
    name: 'ClientBookingBuddyStatus',
    description: 'Status of a client booking buddy relationship',
  })
export type ClientBookingBuddyStatus = z.infer<typeof clientBookingBuddyStatusSchema>

// ClientBookingBuddy record schema for database
export const clientBookingBuddyRecordSchema = z.object({
  clientId: z.string().uuid(),
  buddyClientId: z.string().uuid(),
  status: clientBookingBuddyStatusSchema,
  initiatedBy: z.string().uuid(),
  invitedAt: z.date(),
  acceptedAt: z.date().nullable(),
  revokedAt: z.date().nullable(),
  revokedBy: z.string().uuid().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().nullable(),
})
export type ClientBookingBuddyRecordSchema = z.infer<typeof clientBookingBuddyRecordSchema>

// ClientBookingBuddy public schema (for GraphQL, excludes deletedAt)
export const clientBookingBuddyPublicSchema = clientBookingBuddyRecordSchema
  .omit({ deletedAt: true })
  .register(asObjectType, {
    name: 'ClientBookingBuddy',
    description: 'Client booking buddy relationship record',
  })
export type ClientBookingBuddyPublicSchema = z.infer<typeof clientBookingBuddyPublicSchema>

/**
 * OC-8 / V1 bridge — one linked-account row for the buddy flows. Projects a BUDDY ClientRelation
 * leg with the OTHER party's Client display fields and (optionally) that party's booking on a
 * given session. Serves the legacy /buddy linked-accounts + session buddy-bookings endpoints via
 * the gateway (the api server must not read the database directly).
 */
export const bookingBuddyLinkedAccountSchema = z
  .object({
    clientId: z.uuid(),
    buddyClientId: z.uuid(),
    status: clientBookingBuddyStatusSchema,
    initiatedBy: z.uuid().nullable(),
    acceptedAt: z.date().nullable(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    email: z.string().nullable(),
    profileImageUrl: z.string().nullable(),
    sessionBookingStatus: z.string().nullable(),
    sessionBookingSource: z.string().nullable(),
  })
  .register(asObjectType, {
    name: 'BookingBuddyLinkedAccount',
    description: 'A buddy relationship row with the counterparty display fields',
  })
export type BookingBuddyLinkedAccount = z.infer<typeof bookingBuddyLinkedAccountSchema>

export const bookingBuddyMutationResultSchema = z
  .object({
    success: z.boolean(),
    message: z.string(),
  })
  .register(asObjectType, {
    name: 'BookingBuddyMutationResult',
    description: 'Result of a booking-buddy mutation',
  })
export type BookingBuddyMutationResult = z.infer<typeof bookingBuddyMutationResultSchema>

export const buddyInviteTokenResultSchema = z
  .object({
    inviteToken: z.string(),
  })
  .register(asObjectType, {
    name: 'BuddyInviteTokenResult',
    description: 'A minted single-use buddy share-link invite token',
  })
export type BuddyInviteTokenResult = z.infer<typeof buddyInviteTokenResultSchema>

export const buddyInviteContextSchema = z
  .object({
    valid: z.boolean(),
    inviterFirstName: z.string().nullable(),
    inviterLastName: z.string().nullable(),
    inviterProfileImageUrl: z.string().nullable(),
  })
  .register(asObjectType, {
    name: 'BuddyInviteContext',
    description:
      'Read-only share-link check plus the inviter display fields the accept sheet renders; ' +
      'the image is a freshly signed URL because the one embedded in the link expires',
  })
export type BuddyInviteContext = z.infer<typeof buddyInviteContextSchema>

// Lookup client form schema for buddy search
export const lookupClientFormSchema = z
  .object({
    firstName: z.string().nullish(),
    lastName: z.string().nullish(),
    email: z.string().optional(),
    phone: z
      .string()
      .optional()
      .refine((val) => !val || /^\d*$/.test(val), 'Phone must contain only numbers'),
  })
  .refine(
    (data) => {
      const filledFields = Object.values(data).filter(
        (value) => typeof value === 'string' && value.trim().length > 0
      )
      return filledFields.length >= 2
    },
    { message: 'At least 2 fields must be filled' }
  )

export type LookupClientFormSchema = z.infer<typeof lookupClientFormSchema>

/**
 * Source of a communication preferences update, as an external caller may claim
 * it. This is the GraphQL-facing enum, and the values are unchanged.
 *
 * STAFF is the default and deliberately the most restricted: staff may record an
 * opt-OUT but never re-enable a channel a member turned off, so a member's own
 * withdrawal of consent cannot be undone from the admin UI.
 */
export const externalCommPrefsSourceSchema = z.enum(['CLIENT', 'STAFF']).register(asEnumType, {
  name: 'CommPrefsSource',
  description: 'Who is making the communication preferences update',
})

/**
 * Internal superset, adding INTEGRATION: a connected system relaying the
 * member's own action (a HubSpot unsubscribe link, or re-subscribing through a
 * marketing form). It may re-enable a channel, because it carries the member's
 * intent, but stays distinct from CLIENT so the audit trail shows a machine
 * applied it rather than the member acting in Hapana.
 *
 * INTEGRATION is deliberately absent from the GraphQL enum above. `commPrefsSource`
 * is an argument on the public `updateClient` mutation, so exposing it would let
 * any authenticated caller claim to be an integration and walk through the STAFF
 * restriction. Server-side callers pass it straight to the service instead.
 */
export const commPrefsSourceSchema = z.enum(['CLIENT', 'STAFF', 'INTEGRATION'])
export type CommPrefsSource = z.infer<typeof commPrefsSourceSchema>

// Communication preferences schema for JSON field (not registered as GraphQL type - used internally)
export const clientCommunicationPreferencesSchema = z.object({
  email: z.boolean().optional(),
  sms: z.boolean().optional(),
  phone: z.boolean().optional(),
  notifications: z.boolean().optional(),
})

// Form schema for editing communication preferences (includes UI-only unsubscribeFromEverything)
export const editCommunicationsFormSchema = z.object({
  email: z.boolean(),
  sms: z.boolean(),
  unsubscribeFromEverything: z.boolean(), // UI-only field, not sent to backend
})

export type EditCommunicationsFormSchema = z.infer<typeof editCommunicationsFormSchema>

// Intro offer schema for JSON array field (not registered as GraphQL type - used internally)
export const clientIntroOfferSchema = z.object({
  offerId: z.string(),
  siteId: z.string().optional(),
  claimedAt: z.string(),
  usedAt: z.string().optional(),
})

export type ClientIntroOfferSchema = z.infer<typeof clientIntroOfferSchema>

// Schema for questionnaire data array (as sent from frontend)
export const questionnaireAnswersObjectSchema = z.array(
  z.object({
    questionId: z.string(),
    question: z.string(),
    answer: z.string(),
    type: z.string(),
    formId: z.string(),
    formName: z.string(),
    expectedRevision: z.number().int().nullable().optional(),
    signupAttemptId: z.uuid().nullable().optional(),
  })
)

// Client source enum - where the client was created from
export const ClientSourceEnum = z.enum(CLIENT_SOURCE_VALUES).register(asEnumType, {
  name: 'ClientSource',
  description: 'Source of client creation',
})

export type ClientSourceValue = z.infer<typeof ClientSourceEnum>

/**
 * Enrollment source labels — the subset of ClientSource values shown in the
 * "How did you hear about us?" dropdown. Keyed by ClientSourceValue so the
 * compiler catches any drift with the enum.
 */
export const ENROLLMENT_SOURCE_LABELS: Partial<Record<ClientSourceValue, string>> = {
  MIGRATION_IMPORTED: 'Migration/Imported Data',
  APP_WEB_SIGNUP: 'App/Web Signup',
  BUSINESS_ADDED: 'Business Added',
  EMBED_CREATED: 'Embed Created',
  THIRD_PARTY: 'Third Party',
  CLIENT_REFERRAL: 'Client Referral',
  SOCIAL_MEDIA: 'Social Media',
}

// Main client schema (matches the Client table structure exactly)
export const ClientTypeEnum = z.enum(['PERSON', 'ORGANISATION']).register(asEnumType, {
  name: 'ClientType',
  description: 'Whether a client is a person or an organisation that pays for its employees',
})

export type ClientType = z.infer<typeof ClientTypeEnum>

export const clientSchema = z.object({
  clientId: z.string(),
  firebaseId: z.string().nullish(),
  firstName: z.string().max(50, 'First name must be 50 characters or less'),
  lastName: z.string().max(50, 'Last name must be 50 characters or less'),
  email: z.string().email('Invalid email format').max(50, 'Email must be 50 characters or less'),
  gender: z.string().nullish(),
  mobilePhoneNumber: z.string().nullish(),
  homePhoneNumber: z.string().nullish(),
  address: clientAddressSchema.nullable(),
  dateOfBirth: z.string().date().nullish(),
  occupation: z.string().nullish(),
  fobNumber: z.string().nullable().optional(),
  // Corporate-level barcode (OC-208) — one per client across all brand sites,
  // mirroring fobNumber. Was previously site-scoped on ClientSite.barcode.
  barcode: z.string().nullish(),
  customerId: z.string().nullish(),
  emergencyContact: clientEmergencyContactSchema.nullable(),
  communicationPreferences: clientCommunicationPreferencesSchema.nullable(),
  homeSiteId: z.string().nullish(),
  homeSiteName: z.string().nullish(),
  recentSiteId: z.string().nullish(),
  homeLocationIds: z.array(z.string().uuid()).nullish(), // Sites where client has active services
  activePackagesCount: z.number().int().nullish(), // Total count of active services/packages
  introOffersClaimed: z.array(clientIntroOfferSchema).nullable(),
  source: ClientSourceEnum.nullish(), // Track client origin
  // OC-1492 — set only on clients created from a marketplace contact. Promotion clears it
  // rather than writing false, so the column stays sparse and NULL means not-a-prospect.
  isProspect: z.boolean().nullish(),
  profileImageJSON: imageFileConfigFieldSchema.nullish(),
  favoriteSiteIds: z.array(z.string()).nullable(),
  attendanceCount: z.number().default(0),
  joinDate: z.date(),
  createdAt: z.date(),
  updatedAt: z.date(),
  corporateId: z.string().uuid().nullish(),
  // OC-1296: NULL reads as PERSON, so rows written before organisations existed keep their meaning.
  clientType: ClientTypeEnum.nullish(),
  companyName: z.string().max(255).nullish(),
  taxIdentifier: z.string().max(50).nullish(),
  billingEmail: z.string().max(320).nullish(),
})

/**
 * The credential half of a client, for cohort reads that resolve who exists and what code
 * they carry without paying for the full row (INT-314's per-site door reconcile, INT-747's
 * eligibility-feed barcode resolution).
 */
export const clientAccessCredentialSchema = clientSchema.pick({
  clientId: true,
  fobNumber: true,
  barcode: true,
  dateOfBirth: true,
  gender: true,
})

export type ClientAccessCredential = z.infer<typeof clientAccessCredentialSchema>

// Client alert types constant
export const CLIENT_ALERT_TYPES = [
  {
    type: 'PAYMENT',
    message: 'Payment overdue for membership',
    severity: 'error',
    priority: 1,
  },
  {
    type: 'MEMBERSHIP',
    message: 'Membership expires in 7 days',
    severity: 'warning',
    priority: 2,
  },
  {
    type: 'COMMUNICATION',
    message: 'Class credits running low',
    severity: 'info',
    priority: 3,
  },
  {
    type: 'DOCUMENT',
    message: 'Liability waiver needs to be signed',
    severity: 'error',
    priority: 1,
  },
  {
    type: 'EMAIL',
    message: 'No activity in the last 30 days',
    severity: 'warning',
    priority: 2,
  },
  {
    type: 'GENERAL',
    message: 'General notification',
    severity: 'info',
    priority: 3,
  },
] as const

export const clientAlertInputSchema = z.object({
  id: z.string().nullish(),
  clientId: z.string().nullish(),
  dateOfBirth: z.iso.date().nullable().optional(),
  address: z.unknown().optional(),
  activeServicesCount: z.number().nullable().optional(),
  failedPaymentsCount: z.number().nullable().optional(),
  outstandingPaymentsCount: z.number().nullable().optional(),
  formsPendingCount: z.number().nullable().optional(),
  notesCount: z.number().nullable().optional(),
  propertiesCount: z.number().nullable().optional(),
  isSuspended: z.boolean().nullable().optional(),
  pendingAgreementsCount: z.number().nullable().optional(),
  attendanceCount: z.number().nullable().optional(),
  introOffersClaimed: z.array(clientIntroOfferSchema).nullable().optional(),
  siteId: z.string().nullable().optional(),
  homeSiteId: z.string().nullable().optional(),
  homeSiteName: z.string().nullable().nullish(),
})

export type ClientAlertInput = z.infer<typeof clientAlertInputSchema>

export const clientAlertItemSchema = z
  .object({
    alertId: z.string().optional(),
    title: z.string().optional(),
    description: z.string().nullish(),
    startDate: z.string().nullish(),
    endDate: z.string().nullish(),
  })
  .register(asObjectType, {
    name: 'ClientAlertItem',
    description: 'A single alert within an aggregated alert icon (e.g. one custom alert)',
    extensions: {
      directives: {
        shareable: true,
      },
    },
  })

export type ClientAlertItem = z.infer<typeof clientAlertItemSchema>

export const clientAlertDataSchema = z
  .object({
    count: z.number().optional(),
    failed: z.number().optional(),
    outstanding: z.number().optional(),
    notes: z.number().optional(),
    properties: z.number().optional(),
    dateOfBirth: z.string().optional(),
    address: z.string().optional(),
    isSuspended: z.boolean().optional(),
    homeSiteName: z.string().optional(),
    alertId: z.string().optional(),
    title: z.string().optional(),
    description: z.string().optional(),
    startDate: z.string().optional(),
    endDate: z.string().optional(),
    items: z.array(clientAlertItemSchema).optional(),
  })
  .register(asObjectType, {
    name: 'ClientAlertData',
    description: 'Data payload for client alerts',
    extensions: {
      directives: {
        shareable: true,
      },
    },
  })

// Alert schema - simplified structure for dynamic alert system
export const clientAlertSchema = z
  .object({
    clientId: z.string(), // Client ID for fetching additional data if needed
    type: z.string(), // payment, forms, notesProps, packages, birthday, location, suspended, etc.
    // Data payload for simple alerts - different structure based on alert type
    data: clientAlertDataSchema.optional(),
    // Flag to indicate if this alert needs to fetch additional data on hover
    requiresFetch: z.boolean().default(false),
  })
  .register(asObjectType, {
    name: 'ClientAlert',
    description: 'Client alert with dynamic data based on type',
    extensions: {
      directives: {
        shareable: true,
      },
    },
  })

// A site at which the client has one or more notes. Feeds the check-in note indicator (OC-173) —
// presence per site, sourced from ClientSite.notesCount (same signal as the client-table notesProps).
export const clientNoteSiteSchema = z
  .object({
    id: z.string(),
    siteId: z.string(),
  })
  .register(asObjectType, {
    name: 'ClientNoteSite',
    description: 'A site at which the client has one or more notes',
    extensions: {
      directives: {
        shareable: true,
      },
    },
  })

// Client-Site relationship schemas (defined before clientPublicSchema to avoid initialization error)
export const sitePreferencesSchema = z.object({
  notifications: z.boolean().optional(),
  communications: z.string().optional(),
})

// Named, because this one reaches the woven GraphQL schema through siteSpecificDataSchema and
// GQLoom cannot convert an anonymous enum ("Enum (CHECK_IN) must have a name").
export const clientSiteLinkSourceEnumSchema = z
  .enum(Object.values(ClientSiteLinkSource) as [string, ...string[]])
  .register(asEnumType, {
    name: 'ClientSiteLinkSource',
    description: 'How an automatically created client-site link came to exist',
  })

export const siteSpecificDataSchema = z.object({
  enrollmentSource: z.string().optional(),
  // Distinct from enrollmentSource, which is staff-authored and rendered on the client
  // profile. This one is system-written provenance for links created automatically —
  // see ClientSiteLinkSource.
  linkSource: clientSiteLinkSourceEnumSchema.optional(),
  membershipType: z.string().optional(),
  notes: z.string().optional(),
  preferences: sitePreferencesSchema.optional(),
  useAccountCreditsOnRecurringInvoices: z.boolean().optional(),
})

/**
 * The mutation-input face of {@link siteSpecificDataSchema}. `linkSource` records how a
 * ClientSite link came to exist and is written only by the code that creates one
 * automatically; leaving it on the input face would let a caller stamp `CHECK_IN` on a
 * manual add and pass it off as an automatic visit link, which is exactly what anything
 * reading the marker later must be able to trust.
 */
export const siteSpecificDataInputSchema = siteSpecificDataSchema.omit({ linkSource: true })

// Individual site relationship schema (includes siteId for GraphQL compatibility)
export const siteRelationshipSchema = z
  .object({
    siteId: z.string(), // Include siteId in the object for GraphQL
    joinDate: z.date(),
    siteSpecificData: siteSpecificDataSchema.nullable(),
    createdAt: z.date(),
    updatedAt: z.date(),
    lastBillingDate: z.date().nullish(),
    lastAttendanceDate: z.date().nullish(),
    lastBookingDate: z.date().nullish(),
    creditAccountBalance: z.string().nullish(), // Account credits balance (Big on backend, string on frontend)
  })
  .register(asObjectType, {
    name: 'ClientSiteRelationship',
    description: 'Client relationship to a specific site',
  })

// Define site relationships as an array of objects
export const siteRelationshipsSchema = z.array(siteRelationshipSchema).register(asObjectType, {
  name: 'ClientSiteRelationships',
  description: 'Client relationships to various sites as an array',
})

// Schema for custom property with optional client value
// This combines site-level custom property definition with client-specific value
export const customPropertyWithClientValueSchema = z
  .object({
    // All customPropertySchema fields
    id: z.string(),
    siteId: z.string(),
    name: z.string(),
    category: z.string(),
    type: z.string(),
    options: z
      .array(
        z.object({
          id: z.string().nullish(),
          option: z.string(),
          displayOrder: z.number().nullish(),
        })
      )
      .optional(),
    enableClientEditing: z.boolean(),
    growId: z.string().nullable().nullish(),
    hasSubtext: z.boolean(),
    subtext: z.string().nullable().nullish(),
    alerts: z
      .array(
        z.object({
          id: z.string().nullish(),
          propertyId: z.string().nullish(),
          operator: z.string(),
          value: z.string().nullable().nullish(),
          isDisplayPropertyValueAsAlert: z.boolean(),
          text: z.string().nullable().nullish(),
          createdAt: z.date().optional(),
        })
      )
      .optional(),
    createdAt: z.date().optional(),
    updatedAt: z.date().optional(),
    deletedAt: z.date().nullable().optional(),
    // Optional client value
    clientValue: z
      .object({
        customPropertyValueId: z.string(),
        value: z.string().nullable(), // Allow null values (e.g., when property is cleared)
        updatedAt: z.date(),
      })
      .nullable()
      .optional(),
  })
  .register(asObjectType, {
    name: 'CustomPropertyWithClientValue',
    description: 'A site custom property definition with optional client value',
  })

// Site info schema for siteInformation field (includes nested site object with name)
export const siteInfoSchema = z
  .object({
    siteId: z.string(),
    site: z.object({
      name: z.string(),
    }),
    joinDate: z.date().nullable(),
    checkInCount: z.number().int().default(0),
    lastCheckInOn: z.date().nullable().optional(),
  })
  .register(asObjectType, {
    name: 'ClientSiteInfo',
    description: 'Client site information with site details',
  })

export const siteInformationSchema = z.array(siteInfoSchema).register(asObjectType, {
  name: 'ClientSiteInformation',
  description: 'Client connected locations with site details',
})

// Per-location membership status for the corporate client search's stacked
// Location/Status cells (OC-636): one entry per ACTIVE membership, with the
// client's computed status AT that site.
export const clientLocationStatusSchema = z
  .object({
    siteId: z.string(),
    siteName: z.string().nullable(),
    status: z.string(),
  })
  .register(asObjectType, {
    name: 'ClientLocationStatus',
    description: "A client's computed membership status at one of their connected locations",
  })

// Public schema for GraphQL with frontend compatibility fields
export const clientPublicSchema = clientSchema
  .omit({
    clientId: true,
    profileImageJSON: true,
  })
  .extend({
    id: z.string(), // Maps to clientId for frontend compatibility
    // Imported clients can have no email (22 active at P3 Burleigh Heads); a required email
    // here failed the whole clients page they appeared on, and their profile (INT-1539).
    email: z.string().nullable(),
    name: z.string(), // Computed: firstName + lastName
    membershipStatus: z.string().default('membership'), // Computed based on memberships/packages
    lastVisit: z.string().optional(), // Computed from visit records
    lastBooking: z.string().optional(), // Computed from booking records
    alerts: z.array(clientAlertSchema).default([]), // Computed from alert system
    notes: z.array(z.string()).default([]), // Computed from notes system
    profile: z.string().optional(), // Additional profile data
    totalCheckInCount: z.number().default(0), // Total attendance count across all sites
    siteRelationships: siteRelationshipsSchema.default([]), // Object with siteId keys containing relationship data
    siteInformation: siteInformationSchema.default([]), // Connected locations with site details
    smartGroups: z.array(staffUserSmartGroupPublicSchema).default([]), // Array of smart groups
    customProperties: z.array(customPropertyWithClientValueSchema).optional(), // Custom properties with client values merged
    barcode: z.string().nullish(), // Corporate-level barcode from Client (OC-208) — nullable (barcode-less clients)
    isSuspended: z.boolean().optional(), // Site-level suspension/hold flag from ClientSite
    clientSiteStatus: z.string().nullish(),
    // ClientSite.siteId of the viewing site when the row is a member there. In a
    // cross-site (corporate) search this stays null for clients not at the viewing
    // site — the web Clients list uses that to offer "Copy to this location" (OC-636).
    siteId: z.string().nullish(),
    // Status per connected location; only fetched by the corporate client search
    // (stacked Location/Status cells) — resolved via a batched dataloader.
    locationStatuses: z.array(clientLocationStatusSchema).default([]),
  })
  .register(asObjectType, {
    name: 'Client',
    description: 'A gym client/member',
    extensions: {
      directives: {
        key: {
          fields: 'id',
        },
      },
    },
  })

// Entity stub for services that reference Client but don't resolve it (e.g., payments service)
// Only includes the key field; federation will fetch other fields from the users service
export const clientEntityStubSchema = clientPublicSchema
  .pick({
    id: true,
  })
  .register(asObjectType, {
    name: 'Client',
    extensions: {
      directives: {
        key: {
          fields: 'id',
          resolvable: false,
        },
      },
    },
  })

export const clientReferenceSchema = clientPublicSchema
  .pick({
    id: true,
  })
  .extend({
    // Optional name field populated from invoice calculated field
    name: z.string().optional(),
    // Optional firstName/lastName for fallback when name is null
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    // Optional siteId for passing booking context to reference resolver
    // This enables site-specific data fetching (like alerts) when resolving client from booking
    siteId: z.string().optional(),
    // Optional contact fields for client display in payment listings
    email: z.string().optional(),
    mobilePhoneNumber: z.string().optional(),
    // Client alerts (payment failures, missing info, etc.)
    alerts: z.array(clientAlertSchema).optional(),
  })

// Admin schema for GraphQL - uses clientId instead of id for admin queries
// Computed fields are optional since admin queries return raw database data
export const clientAdminPublicSchema = clientSchema
  .omit({
    profileImageJSON: true,
  })
  .extend({
    // Keep clientId (from base schema) and add optional computed fields
    id: z.string().optional(), // Maps to clientId for frontend compatibility
    email: z.string().nullable(), // A member may have no email (INT-1886); non-null failed the whole list
    name: z.string().optional(), // Computed: firstName + lastName
    membershipStatus: z.string().optional(), // Computed based on memberships/packages
    lastVisit: z.string().optional(), // Computed from visit records
    lastBooking: z.string().optional(), // Computed from booking records
    alerts: z.array(clientAlertSchema).optional(), // Computed from alert system
    notes: z.array(z.string()).optional(), // Computed from notes system
    profile: z.string().optional(), // Additional profile data
    siteRelationships: siteRelationshipsSchema.optional(), // Object with siteId keys containing relationship data
    smartGroups: z.array(staffUserSmartGroupPublicSchema).optional(), // Array of smart groups
  })
  .register(asObjectType, {
    name: 'ClientAdmin',
    description: 'A gym client/member (admin view with clientId)',
  })

// Create client input schema - accepts form data (strings for JSON fields)
export const clientCreateSchema = clientSchema
  .omit({
    clientId: true,
    profileImageJSON: true,
    createdAt: true,
    updatedAt: true,
    joinDate: true,
    homeSiteName: true,
    fobNumber: true,
    barcode: true,
    address: true,
    emergencyContact: true,
    communicationPreferences: true,
    introOffersClaimed: true,
    attendanceCount: true,
    favoriteSiteIds: true,
    corporateId: true,
    clientType: true,
    companyName: true,
    taxIdentifier: true,
    billingEmail: true,
  })
  .extend({
    enrollmentSource: z.string().optional(),
    sendWelcomeEmail: z.boolean().optional(),
    // Override dateOfBirth to accept ISO strings - backend will handle conversion
    dateOfBirth: z.string().optional(),
    // Staff/CSV-supplied barcode → resolved to the corporate-level Client.barcode (OC-208)
    barcodeId: z.string().trim().regex(/^\S*$/, 'Barcode cannot contain spaces').optional(),
    // Allow homeSiteId to be set during creation (user's default site)
    homeSiteId: z.string().uuid().optional(),
    // Allow recentSiteId to be set during creation (most recently visited site)
    recentSiteId: z.string().uuid().optional(),
    // Accept address and emergency contact as objects from forms
    address: clientAddressSchema.optional(),
    emergencyContact: clientEmergencyContactSchema.optional(),
    // Custom properties as array (stored in ClientCustomPropertyValue table)
    customProperties: z
      .array(
        z.object({
          siteCustomPropertyId: z.string(),
          value: z.string(),
        })
      )
      .optional(),
    // Questionnaire answers as array (not stored in Client table, but in ClientSite siteSpecificData)
    questionnaireAnswers: questionnaireAnswersObjectSchema.optional(),
  })
  .register(asObjectType, {
    name: 'CreateClientInput',
    description: 'Input for creating a new client',
  })

// Update client input schema
export const clientUpdateSchema = clientSchema
  .omit({
    clientId: true,
    profileImageJSON: true,
    createdAt: true,
    updatedAt: true,
    joinDate: true,
    corporateId: true,
    clientType: true,
    companyName: true,
    taxIdentifier: true,
    billingEmail: true,
    // MEM-812: a computed counter, never written by a profile save. Its .default(0) still
    // fires under .partial() in Zod v4, so keeping it here reset the brand total on every save.
    attendanceCount: true,
    // OC-1383: purchase history, not a profile field. `updateClient` admits a member's own
    // token (@StaffPermissionsOrClientSelf) and the service spreads input straight to the
    // repository, where a `null` becomes a literal SQL NULL — so leaving this writable let
    // any caller wipe a member's whole claim history. Only
    // `ClientsRepository.appendIntroOfferClaimed` may write it.
    introOffersClaimed: true,
  })
  .extend({
    // Corporate-level barcode (OC-208) — written to Client.barcode, one per client
    barcode: z.string().trim().regex(/^\S*$/, 'Barcode cannot contain spaces').optional(),
    // MEM-301: the scanned card serial. Write-only and NOT a Client column — it is
    // stored on a ClientCredential row, and the derived door value it produces is what
    // lands on Client.fobNumber. Null clears the member's fob.
    csn: z.string().nullish(),
    // INT-577: staff have confirmed that this fob may be taken from whoever holds it.
    // Write-only and not a Client column. Inert on its own — it is read only after a
    // uniqueness conflict has already been found, and its absence keeps the conflict a
    // hard error, so a fob can never change hands without someone deciding it should.
    confirmFobReassign: z.boolean(),
  })
  .partial()
  .register(asObjectType, {
    name: 'UpdateClientInput',
    description: 'Input for updating a client',
  })

/**
 * Bulk-import input shape for `ClientsService.bulkCreateForImport`.
 *
 * Extends {@link clientCreateSchema} with a required `siteId` (the site the
 * client is being imported into — used for the ClientSite write) and an
 * optional `joinDate` override so CSV importers can preserve a historical
 * join date on the ClientSite row. The per-row `createClient` does not
 * accept a join-date override (always uses `now`); this is the single
 * additive deviation from pure mirror and is documented in the wrapper
 * JSDoc.
 *
 * Intentional deviations from pure-mirror `createClient`:
 * - Firebase user creation is NOT performed.
 * - Welcome email is NOT sent.
 * - `_publishClientCreatedSideEffects` (audit + Grow + IntegrationEventService.onClientCreated) is NOT called.
 * - `ClientSitesService.addClientToSite`'s per-call audit publish is NOT replicated.
 * - `resolveBarcode`'s priority-2 (corporate-link copy) and priority-3 (autoAssign generation) are NOT applied — only priority-1 (CSV-supplied `barcodeId`) is honored.
 */
export const clientBulkCreateForImportSchema = clientCreateSchema.safeExtend({
  siteId: z.uuid(),
  joinDate: z.date().optional(),
  // INT-1341: `clientCreateSchema` omits communicationPreferences (the per-row create
  // input doesn't accept it), but the Client import's marketing opt-in columns write it
  // on insert so a migrated brand starts with the consent the source actually recorded.
  communicationPreferences: clientCommunicationPreferencesSchema.optional(),
  // fobNumber is a real Client column that clientCreateSchema omits (the per-row
  // create input doesn't accept it); CSV import writes it directly.
  fobNumber: z.string().optional(),
})

/**
 * Bulk-import input shape for `ClientsService.bulkUpdateForImport`.
 *
 * Each entry pairs a target `clientId` with a partial {@link clientUpdateSchema}.
 * The wrapper mirrors `updateClient`'s pre-repo validation (firstName / lastName /
 * email non-empty when present, email format, global email-uniqueness check) and
 * forwards to {@link ClientsRepository.bulkUpdateForImport}.
 *
 * Intentional deviations from pure-mirror `updateClient`:
 * - `communicationPreferences`: STAFF-source disabled-channel re-enable
 *   filter is mirrored at the wrapper via a batched current-prefs pre-fetch.
 *   Imports run as `commPrefsSource = 'STAFF'`; explicit `null` (wipe) passes
 *   through.
 * - `publishMemberUpdatedAudit` is NOT called.
 * - `GrowEventService.publishClientUpdated` is NOT called.
 * - `CHANGED_EMAIL_ADDRESS` notification is NOT sent on email change.
 * - Per-row barcode update (`ClientSitesService.updateBarcode`) is NOT done —
 *   the caller writes site-scoped fields directly on ClientSite.
 */
export const clientBulkUpdateForImportSchema = z.object({
  clientId: z.uuid(),
  fields: clientUpdateSchema,
})

// Input schemas for GraphQL queries
export const clientsFilterInputSchema = z
  .object({
    search: z.string().optional(),
    status: z.string().optional(),
    siteId: z.string().optional(),
    tab: z.string().optional(),
  })
  .register(asObjectType, {
    name: 'ClientsFilterInput',
    description: 'Filter options for clients query',
  })

export const clientsSortInputSchema = z
  .object({
    field: z.string().optional(),
    direction: clientSortDirectionEnumSchema.optional(),
  })
  .register(asObjectType, {
    name: 'ClientsSortInput',
    description: 'Sort options for clients query',
  })

// Re-export clientFiltersSchema for backward compatibility
export { clientFiltersSchema, type ClientFiltersSchema } from './client-filter-schemas'

const clientOrderByEnumSchema = z
  .enum(['firstName', 'lastName', 'email', 'createdAt', 'updatedAt'])
  .register(asEnumType, {
    name: 'ClientOrderBy',
  })

export const clientPaginationArgsSchema = createPaginationWithCursorArgsSchema()
  .withSearch()
  .withOrderBy(clientOrderByEnumSchema)
  .build()
  .omit({ orderBy: true }) // Remove the required orderBy
  .extend({
    siteId: z.string().optional(),
    crossSiteSearch: z.boolean().optional(),
    // When true, the site-specific Clients list returns DEACTIVATED members
    // (soft-deleted ClientSite links, deletedAt IS NOT NULL) instead of active
    // ones — the "Show deactivated" view from which staff reactivate them.
    showDeactivated: z.boolean().optional(),
    includeSharedSiteVisitors: z.boolean().optional(),
    // OC-1492: prospects are marketplace contacts who have never engaged with the club.
    // They are excluded by default so they cannot drown the member list; the Clients
    // page toggle opts them back in.
    showProspects: z.boolean().optional(),
    // Individual field search parameters for corporate lookup
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    email: z.string().optional(),
    phone: z.string().optional(),
    filters: clientFiltersSchema.optional(),
    // Add orderBy back as optional
    orderBy: z
      .array(
        z.object({
          column: clientOrderByEnumSchema,
          direction: orderByDirectionEnumSchema,
        })
      )
      .optional(),
  })
  .register(asObjectType, {
    name: 'ClientPaginationArgs',
    description: 'Arguments for client pagination queries with site ID and filters object',
  })

// Corporate clients pagination schema (cross-site search without required siteId)
export const corporateClientPaginationArgsSchema = createPaginationWithCursorArgsSchema()
  .build()
  .extend({
    siteId: z.string().optional(), // Optional for corporate search
    // Individual field search parameters for corporate lookup
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    email: z.string().optional(),
    phone: z.string().optional(),
  })
  .register(asObjectType, {
    name: 'CorporateClientPaginationArgs',
    description: 'Arguments for corporate client pagination queries (cross-site search)',
  })

export const clientPaginationResultSchema =
  createPaginationWithCursorResultSchema(clientPublicSchema)

export const clientAdminPaginationResultSchema =
  createPaginationWithCursorResultSchema(clientAdminPublicSchema)

export type ClientPaginationWithCursorArgsSchema = z.infer<typeof clientPaginationArgsSchema>
export type CorporateClientPaginationArgsSchema = z.infer<
  typeof corporateClientPaginationArgsSchema
>

// Page info schema
export const clientsPageInfoSchema = z
  .object({
    hasNextPage: z.boolean(),
    hasPreviousPage: z.boolean(),
    startCursor: z.string().optional(),
    endCursor: z.string().optional(),
    totalRecords: z.number(),
  })
  .register(asObjectType, {
    name: 'ClientsPageInfo',
    description: 'Pagination information for clients',
  })

// Clients response schema
export const clientsResponseSchema = z
  .object({
    results: z.array(
      z.object({
        node: clientPublicSchema,
        cursor: z.string().optional(),
      })
    ),
    pageInfo: clientsPageInfoSchema,
  })
  .register(asObjectType, {
    name: 'ClientsResponse',
    description: 'Response for clients query',
  })

// Client tab schema
export const clientTabSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    value: z.string(),
    label: z.string(),
    count: z.number(),
    enabled: z.boolean(),
    description: z.string().optional(),
  })
  .register(asObjectType, {
    name: 'ClientTab',
    description: 'Client tab for filtering',
  })

// Available client tabs response
export const availableClientTabsResponseSchema = z
  .object({
    tabs: z.array(clientTabSchema),
  })
  .register(asObjectType, {
    name: 'AvailableClientTabsResponse',
    description: 'Available client tabs for filtering',
  })

// Client stats schema
export const clientStatsResponseSchema = z
  .object({
    totalClients: z.number(),
    activeClients: z.number(),
    newThisMonth: z.number(),
    churnRate: z.number(),
  })
  .register(asObjectType, {
    name: 'ClientStatsResponse',
    description: 'Client statistics',
  })

// Enrollment source schema
export const enrollmentSourceSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    description: z.string().optional(),
  })
  .register(asObjectType, {
    name: 'EnrollmentSource',
    description: 'Source where a client can be enrolled from',
  })

// Enrollment sources response schema
export const enrollmentSourcesResponseSchema = z
  .object({
    sources: z.array(enrollmentSourceSchema),
  })
  .register(asObjectType, {
    name: 'EnrollmentSourcesResponse',
    description: 'Response containing available enrollment sources',
  })

// Form submission status enum
export const formSubmissionStatusSchema = z.enum(['PENDING', 'COMPLETED']).register(asEnumType, {
  name: 'FormSubmissionStatus',
  description: 'Status of a form submission',
})

export const formQuestionSchema = z.discriminatedUnion('type', [
  z.object({
    siteFormQuestionId: z.string(),
    title: z.string(),
    type: z.literal('text'),
    orderIndex: z.number(),
  }),
  z.object({
    siteFormQuestionId: z.string(),
    title: z.string(),
    type: z.literal('select'),
    options: z.array(z.string()),
    orderIndex: z.number(),
  }),
  z.object({
    siteFormQuestionId: z.string(),
    title: z.string(),
    type: z.literal('boolean'),
    orderIndex: z.number(),
  }),
  z.object({
    siteFormQuestionId: z.string(),
    title: z.string(),
    type: z.literal('radio'),
    options: z.array(z.string()),
    orderIndex: z.number(),
  }),
  z.object({
    siteFormQuestionId: z.string(),
    title: z.string(),
    type: z.literal('range'),
    options: z.array(z.string()).length(4),
    orderIndex: z.number(),
  }),
  z.object({
    siteFormQuestionId: z.string(),
    title: z.string(),
    type: z.literal('signature'),
    orderIndex: z.number(),
  }),
  z.object({
    siteFormQuestionId: z.string(),
    title: z.string(),
    type: z.literal('textBlurb'),
    options: z.array(z.string()).length(1),
    orderIndex: z.number(),
  }),
])

// Client form answer schema (matches database structure)
export const clientFormAnswerSchema = z
  .object({
    formSubmissionId: z.string(),
    siteFormQuestionId: z.string(),
    answer: z.string(),
    answeredAt: z.date(),
  })
  .register(asObjectType, {
    name: 'ClientFormAnswer',
    description: 'A client form answer',
  })

const formSnapshotFieldSchema = z.unknown().nullish().register(asField, {
  type: GraphQLJSON,
  description: 'Staff only: the form definition pinned when the form was assigned',
})

const scoringSnapshotFieldSchema = z.unknown().nullish().register(asField, {
  type: GraphQLJSON,
  description: 'Staff only: the points awarded and the band chosen at completion',
})

const clientFormSubmissionResultFields = {
  totalScore: z.number().int().nullable().optional(),
  resultLabel: z.string().nullable().optional(),
  resultBandIndex: z.number().int().nullable().optional(),
  resultBandId: z.string().nullable().optional(),
  completedVia: formCompletedViaSchema.nullable().optional(),
  completedDeviceTokenId: z.string().nullable().optional(),
  handoffId: z.string().nullable().optional(),
  requiredBefore: formRequiredBeforeSchema.nullable().optional(),
  supersededAt: z.date().nullable().optional(),
  cancelledAt: z.date().nullable().optional(),
  cancellationReason: z.string().nullable().optional(),
  isCurrentResult: z.boolean().nullable().optional(),
  purgedAt: z.date().nullable().optional(),
  purgeReason: z.string().nullable().optional(),
  formSnapshot: formSnapshotFieldSchema,
  scoringSnapshot: scoringSnapshotFieldSchema,
}

// Client form submission schema (matches database structure)
export const clientFormSubmissionSchema = z
  .object({
    formSubmissionId: z.string(),
    clientId: z.string(),
    siteId: z.string(),
    siteFormId: z.string(),
    formName: z.string(),
    assignedBy: z.string().nullable().optional(),
    status: formSubmissionStatusSchema,
    assignedAt: z.date(),
    completedAt: z.date().nullable().optional(),
    answers: z.array(clientFormAnswerSchema).optional(),
    ...clientFormSubmissionResultFields,
  })
  .register(asObjectType, {
    name: 'ClientFormSubmission',
    description: 'A client form submission with answers',
  })

// Client form submission with client ID (for single submission queries)
export const clientFormSubmissionWithClientSchema = z
  .object({
    formSubmissionId: z.string(),
    clientId: z.string(),
    siteId: z.string(),
    siteFormId: z.string(),
    assignedBy: z.string().nullable().optional(),
    status: formSubmissionStatusSchema,
    assignedAt: z.date(),
    completedAt: z.date().nullable().optional(),
    answers: z.array(clientFormAnswerSchema),
    ...clientFormSubmissionResultFields,
  })
  .register(asObjectType, {
    name: 'ClientFormSubmissionWithClient',
    description: 'A client form submission with client ID and answers',
  })

// Database record schemas (not exposed in GraphQL)
export const clientFormSubmissionRecordSchema = z.object({
  formSubmissionId: z.string(),
  clientId: z.string(),
  siteId: z.string(),
  siteFormId: z.string(),
  assignedBy: z.string().nullish(),
  status: z.string(),
  assignedAt: z.date(),
  completedAt: z.date().nullable().optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
})

export const clientFormAnswerRecordSchema = z.object({
  formSubmissionId: z.string(),
  siteFormQuestionId: z.string(),
  answer: z.string(),
  answeredAt: z.date(),
  createdAt: z.date(),
  updatedAt: z.date(),
})

// Response schemas
export const clientFormSubmissionsResponseSchema = z
  .object({
    submissions: z.array(clientFormSubmissionSchema),
  })
  .register(asObjectType, {
    name: 'ClientFormSubmissionsResponse',
    description: 'Response containing client form submissions',
  })

export const submitClientFormsResponseSchema = z
  .object({
    success: z.boolean(),
    message: z.string().optional(),
    resultVisibleToClient: z.boolean().nullish(),
  })
  .register(asObjectType, {
    name: 'SubmitClientFormsResponse',
    description: 'Response for client form submission',
  })

export const deleteFormSubmissionResponseSchema = z
  .object({
    success: z.boolean(),
    message: z.string().optional(),
  })
  .register(asObjectType, {
    name: 'DeleteFormSubmissionResponse',
    description: 'Response for form submission deletion',
  })

// Form submission pagination schemas
export const formSubmissionFiltersSchema = z
  .object({
    status: z
      .object({
        value: z.string(),
      })
      .optional(),
  })
  .optional()

export const formSubmissionPaginationArgsSchema = z.object({
  clientId: z.string(),
  siteId: z.string(),
  filters: formSubmissionFiltersSchema,
  pageSize: z.number().optional(),
  cursor: z.string().optional(),
  search: z.string().optional(),
})

export const clientFormSubmissionPaginationResultSchema = createPaginationWithCursorResultSchema(
  clientFormSubmissionSchema
)

// Client files pagination schemas
export const clientFilesFiltersSchema = z
  .object({
    fileType: z
      .object({
        value: z.string(),
      })
      .optional(),
  })
  .optional()

export const clientFilesPaginationArgsSchema = z.object({
  clientId: z.string(),
  siteId: z.string(),
  filters: clientFilesFiltersSchema,
  pageSize: z.number().optional(),
  cursor: z.string().optional(),
  search: z.string().optional(),
})

export const clientFilesPaginationResultSchema =
  createPaginationWithCursorResultSchema(clientFileSchema)

// Client agreement schemas — extracted to dedicated file
export {
  agreementTypeEnum,
  agreementStatusEnum,
  agreementMethodEnum,
  clientAgreementSchema,
  siteAgreementTemplateSchema,
  createSiteAgreementTemplateInputSchema,
  signClientAgreementInputSchema,
  sendAgreementResponseSchema,
  clientAgreementsFiltersSchema,
  clientAgreementsPaginationArgsSchema,
  clientAgreementsPaginationResultSchema,
  type ClientAgreementSchema,
  type AgreementTypeSchema,
  type AgreementStatusSchema,
  type AgreementMethodSchema,
  type SiteAgreementTemplateSchema,
  type CreateSiteAgreementTemplateInputSchema,
  type SignClientAgreementInputSchema,
  type SendAgreementResponseSchema,
} from './client-agreements-schemas'

// Input schema for assigning a form to a client
export const assignFormToClientInputSchema = z
  .object({
    clientId: z.string(),
    siteId: z.string(),
    siteFormId: z.string(),
    sendEmail: z.boolean().optional(),
    requiredBefore: formRequiredBeforeSchema.nullable().optional(),
  })
  .register(asObjectType, {
    name: 'AssignFormToClientInput',
    description: 'Input for assigning a form to a client',
  })

// Input schema for submitting form answers
export const submitFormAnswersInputSchema = z
  .object({
    formSubmissionId: z.string(),
    siteId: z.string(),
    answers: z.array(
      z.object({
        siteFormQuestionId: z.string(),
        answer: z.string(),
      })
    ),
    signature: z.string().optional(),
  })
  .register(asObjectType, {
    name: 'SubmitFormAnswersInput',
    description: 'Input for submitting form answers',
  })

export const cancelFormAssignmentInputSchema = z
  .object({
    formSubmissionId: z.string(),
    siteId: z.string(),
    reason: z.string().trim().min(1).max(255),
  })
  .register(asObjectType, {
    name: 'CancelFormAssignmentInput',
    description: 'Input for withdrawing an uncompleted form assignment',
  })

export const cancelFormAssignmentResponseSchema = z
  .object({
    success: z.boolean(),
    message: z.string().optional(),
  })
  .register(asObjectType, {
    name: 'CancelFormAssignmentResponse',
    description: 'Response for withdrawing a form assignment',
  })

export const clientFormResultSchema = z
  .object({
    formSubmissionId: z.string(),
    clientId: z.string(),
    siteId: z.string(),
    siteFormId: z.string(),
    completedAt: z.date().nullable().optional(),
    totalScore: z.number().int().nullable().optional(),
    resultLabel: z.string().nullable().optional(),
    resultDescription: z.string().nullable().optional(),
    resultBandId: z.string().nullable().optional(),
    resultBandIndex: z.number().int().nullable().optional(),
    completionSequence: z.number().int().nullable().optional(),
    completedVia: formCompletedViaSchema.nullable().optional(),
    assessedAt: z.date().nullable().optional(),
    resultSource: formExternalResultSourceSchema.nullable().optional(),
  })
  .register(asObjectType, {
    name: 'ClientFormResult',
    description: "The client's current scored result for a form at a site",
  })

// Response schema for assigning a form
export const assignFormResponseSchema = z
  .object({
    formSubmissionId: z.string(),
    success: z.boolean(),
    message: z.string().optional(),
  })
  .register(asObjectType, {
    name: 'AssignFormResponse',
    description: 'Response for assigning a form',
  })

// Client email status — checks if a client email already exists in the corporate
export const clientEmailStatusInputSchema = z.object({
  email: z.string(),
  siteId: z.uuid(),
})

export const clientEmailStatusSchema = z
  .object({
    exists: z.boolean(),
    clientId: z.uuid().optional(),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    email: z.string().optional(),
    homeSiteName: z.string().optional(),
  })
  .register(asObjectType, {
    name: 'ClientEmailStatus',
    description: 'Status of a client email lookup within a corporate',
  })

export type ClientEmailStatus = z.infer<typeof clientEmailStatusSchema>

// Client mutation response schemas
export const createClientResponseSchema = z
  .object({
    id: z.string(),
  })
  .register(asObjectType, {
    name: 'CreateClientResponse',
    description: 'Response for client creation',
  })

export const updateClientResponseSchema = z
  .object({
    success: z.boolean(),
  })
  .register(asObjectType, {
    name: 'UpdateClientResponse',
    description: 'Response for client update',
  })

export const deleteClientResponseSchema = z
  .object({
    result: clientPublicSchema,
  })
  .register(asObjectType, {
    name: 'DeleteClientResponse',
    description: 'Response for client deletion',
  })

// OC-100: block/unblock (per-site) and anonymise (brand-wide) results.
export const blockClientResponseSchema = z
  .object({
    success: z.boolean(),
    message: z.string().nullish(),
  })
  .register(asObjectType, {
    name: 'BlockClientResponse',
    description: 'Response for blocking or unblocking a client at a site',
  })

export const anonymiseClientResponseSchema = z
  .object({
    success: z.boolean(),
    message: z.string().nullish(),
  })
  .register(asObjectType, {
    name: 'AnonymiseClientResponse',
    description: 'Response for anonymising a client',
  })

// Member-initiated account deletion (mobile app / GDPR / Apple App Store req).
// The `name` MUST stay 'AccountDeletionRequestInput' — the legacy mobile API sends a
// hand-written mutation typed against exactly that name; renaming it re-breaks the
// GRAPHQL_VALIDATION_FAILED "Unknown type AccountDeletionRequestInput" that this fixes.
export const accountDeletionRequestInputSchema = z
  .object({
    clientId: z.string(),
    siteId: z.string(),
    reason: z.string().nullish(),
    feedback: z.string().nullish(),
  })
  .register(asObjectType, {
    name: 'AccountDeletionRequestInput',
    description: 'Input for a member-initiated account deletion request',
  })

export const accountDeletionRequestResultSchema = z
  .object({
    success: z.boolean(),
    message: z.string(),
    requestId: z.string(),
  })
  .register(asObjectType, {
    name: 'AccountDeletionRequestResult',
    description: 'Result of a member-initiated account deletion request',
  })

// Questionnaire answer input schema (for repository layer)
export const questionnaireAnswerInputSchema = z.object({
  questionId: z.string(),
  answer: z.string(),
})

// Questionnaire form input schema (for repository layer)
export const questionnaireFormInputSchema = z.object({
  formId: z.string(),
  answers: z.array(questionnaireAnswerInputSchema),
  expectedRevision: z.number().int().nullable().optional(),
})

// Array of questionnaire forms (for repository layer)
export const questionnaireAnswersInputSchema = z.array(questionnaireFormInputSchema)

// Moved siteSpecificDataSchema definition above clientPublicSchema to avoid initialization error

export const clientSiteSchema = z
  .object({
    clientId: z.string(),
    siteId: z.string(),
    joinDate: z.date(),
    siteSpecificData: siteSpecificDataSchema.nullable(),
    createdAt: z.date(),
    lastBillingDate: z.date().nullish(),
    lastAttendanceDate: z.date().nullish(),
    lastBookingDate: z.date().nullish(),
    creditAccountBalance: z.string().nullish(), // Account credits balance (Big on backend, string on frontend)
    favoriteInstructorIds: z.array(z.string()).optional(), // Array of favorite instructor IDs
    updatedAt: z.date(),
  })
  .register(asObjectType, {
    name: 'ClientSite',
    description: 'A client-site relationship',
  })

export const clientSitesResponseSchema = z
  .object({
    sites: z.array(clientSiteSchema.omit({ clientId: true })),
  })
  .register(asObjectType, {
    name: 'ClientSitesResponse',
    description: 'Response containing client sites',
  })

export const siteClientsResponseSchema = z
  .object({
    clients: z.array(clientSiteSchema.omit({ siteId: true })),
  })
  .register(asObjectType, {
    name: 'SiteClientsResponse',
    description: 'Response containing site clients',
  })

export const clientSiteStatsResponseSchema = z
  .object({
    totalClients: z.number(),
  })
  .register(asObjectType, {
    name: 'ClientSiteStatsResponse',
    description: 'Response containing client site statistics',
  })

export const addClientToSiteResponseSchema = z
  .object({
    success: z.boolean(),
    message: z.string().optional(),
  })
  .register(asObjectType, {
    name: 'AddClientToSiteResponse',
    description: 'Response for adding client to site',
  })

export const removeClientFromSiteResponseSchema = z
  .object({
    success: z.boolean(),
    message: z.string().optional(),
  })
  .register(asObjectType, {
    name: 'RemoveClientFromSiteResponse',
    description: 'Response for removing client from site',
  })

export const updateClientSiteDataResponseSchema = z
  .object({
    success: z.boolean(),
    message: z.string().optional(),
  })
  .register(asObjectType, {
    name: 'UpdateClientSiteDataResponse',
    description: 'Response for updating client site data',
  })

export const transferClientResponseSchema = z
  .object({
    success: z.boolean(),
    message: z.string().optional(),
  })
  .register(asObjectType, {
    name: 'TransferClientResponse',
    description: 'Response for transferring client between sites',
  })

// Combined schema for Client with ClientSite properties (aggregated from DB)
export const clientWithSiteFlatSchema = clientSchema.extend({
  // ClientSite relationships as JSON string from SQL aggregation
  siteRelationshipsJson: z.string().optional(),
})

// Type exports
export type ClientSchema = z.infer<typeof clientSchema>
export type ClientPublicSchema = z.infer<typeof clientPublicSchema>
export type ClientCreateSchema = z.infer<typeof clientCreateSchema>
export type ClientUpdateSchema = z.infer<typeof clientUpdateSchema>
export type ClientCommunicationPreferencesSchema = z.infer<
  typeof clientCommunicationPreferencesSchema
>
export type ClientBulkCreateForImportSchema = z.infer<typeof clientBulkCreateForImportSchema>
export type ClientBulkUpdateForImportSchema = z.infer<typeof clientBulkUpdateForImportSchema>
export type ClientWithSiteFlatSchema = z.infer<typeof clientWithSiteFlatSchema>
export type ClientsFilterInputSchema = z.infer<typeof clientsFilterInputSchema>
export type ClientsSortInputSchema = z.infer<typeof clientsSortInputSchema>
export type EnrollmentSourceSchema = z.infer<typeof enrollmentSourceSchema>
export type EnrollmentSourcesResponseSchema = z.infer<typeof enrollmentSourcesResponseSchema>
export type FormQuestionSchema = z.infer<typeof formQuestionSchema>
export type ClientFormAnswerSchema = z.infer<typeof clientFormAnswerSchema>
export type ClientFormSubmissionSchema = z.infer<typeof clientFormSubmissionSchema>
export type CancelFormAssignmentInputSchema = z.infer<typeof cancelFormAssignmentInputSchema>
export type ClientFormResultSchema = z.infer<typeof clientFormResultSchema>
export type ClientFormSubmissionWithClientSchema = z.infer<
  typeof clientFormSubmissionWithClientSchema
>
export type CreateClientResponseSchema = z.infer<typeof createClientResponseSchema>
export type UpdateClientResponseSchema = z.infer<typeof updateClientResponseSchema>
export type DeleteClientResponseSchema = z.infer<typeof deleteClientResponseSchema>
export type ClientFormSubmissionRecordSchema = z.infer<typeof clientFormSubmissionRecordSchema>
export type ClientFormAnswerRecordSchema = z.infer<typeof clientFormAnswerRecordSchema>
export type FormSubmissionFiltersSchema = z.infer<typeof formSubmissionFiltersSchema>
export type FormSubmissionPaginationArgsSchema = z.infer<typeof formSubmissionPaginationArgsSchema>
export type ClientFormSubmissionPaginationResultSchema = z.infer<
  typeof clientFormSubmissionPaginationResultSchema
>
export type ClientFilesFiltersSchema = z.infer<typeof clientFilesFiltersSchema>
export type ClientFilesPaginationArgsSchema = z.infer<typeof clientFilesPaginationArgsSchema>
export type SitePreferencesSchema = z.infer<typeof sitePreferencesSchema>
export type SiteSpecificDataSchema = z.infer<typeof siteSpecificDataSchema>
export type ClientSiteSchema = z.infer<typeof clientSiteSchema>
export type QuestionnaireAnswerInputSchema = z.infer<typeof questionnaireAnswerInputSchema>
export type QuestionnaireFormInputSchema = z.infer<typeof questionnaireFormInputSchema>
export type QuestionnaireAnswersInputSchema = z.infer<typeof questionnaireAnswersInputSchema>
export type QuestionnaireAnswersObjectSchema = z.infer<typeof questionnaireAnswersObjectSchema>

// Enum for changedByType - registered for GraphQL
// Uses lowercase to match user.type values directly
export const customPropertyChangedByTypeSchema = z
  .enum(['staff', 'client', 'system', 'admin', 'form_submission'])
  .register(asEnumType, {
    name: 'CustomPropertyChangedByType',
    description: 'Type of user who changed a custom property value',
  })

export type CustomPropertyChangedByType = z.infer<typeof customPropertyChangedByTypeSchema>

// Client custom property value schemas
export const clientCustomPropertyValueSchema = z
  .object({
    customPropertyValueId: z.string(),
    clientId: z.string(),
    siteId: z.string(),
    siteCustomPropertyId: z.string(),
    value: z.string().nullable(),
    updatedAt: z.date(),
    changedBy: z.string().nullish(),
    changedByType: customPropertyChangedByTypeSchema.nullish(),
    version: z.number().nullish(),
  })
  .register(asObjectType, {
    name: 'ClientCustomPropertyValue',
    description: 'A client custom property value',
  })

export const clientCustomPropertyValueInputSchema = z
  .object({
    siteCustomPropertyId: z.string(),
    value: z.string(),
  })
  .register(asObjectType, {
    name: 'ClientCustomPropertyValueInput',
    description: 'Input for creating or updating a client custom property value',
  })

// Database-specific schemas (not exposed in GraphQL)
export const clientCustomPropertyValueRecordSchema = z.object({
  customPropertyValueId: z.string(),
  clientId: z.string(),
  siteId: z.string(),
  siteCustomPropertyId: z.string(),
  value: z.string().nullable(), // Allow NULL in database records
  updatedAt: z.date(),
  changedBy: z.string().nullish(),
  changedByType: customPropertyChangedByTypeSchema.nullish(),
  version: z.number().nullish(),
})

// History record schema for versioning
export const clientCustomPropertyValueHistoryRecordSchema = z.object({
  historyId: z.string(),
  customPropertyValueId: z.string(),
  clientId: z.string(),
  siteId: z.string(),
  siteCustomPropertyId: z.string(),
  value: z.string().nullable(),
  version: z.number(),
  changedBy: z.string(),
  changedByType: customPropertyChangedByTypeSchema,
  changedAt: z.date(),
  sourceKey: z.string().nullish(),
})

export const FORM_SUBMISSION_HISTORY_SOURCE_PREFIX = 'form:'

export const buildFormSubmissionSourceKey = (formSubmissionId: string): string =>
  `${FORM_SUBMISSION_HISTORY_SOURCE_PREFIX}${formSubmissionId}`

export const readFormSubmissionIdFromSourceKey = (
  sourceKey: string | null | undefined
): string | null =>
  sourceKey?.startsWith(FORM_SUBMISSION_HISTORY_SOURCE_PREFIX)
    ? sourceKey.slice(FORM_SUBMISSION_HISTORY_SOURCE_PREFIX.length)
    : null

export const CUSTOM_PROPERTY_HISTORY_MAX_PAGE_SIZE = 50

export const clientCustomPropertyValueHistoryArgsSchema = z.object({
  clientId: z.string(),
  siteId: z.string(),
  siteCustomPropertyId: z.string(),
  first: z.number().int().min(1).max(CUSTOM_PROPERTY_HISTORY_MAX_PAGE_SIZE).optional(),
  after: z.string().max(512).nullish(),
})

export const clientCustomPropertyValueHistoryEntrySchema = z
  .object({
    historyId: z.string(),
    value: z.string().nullable(),
    changedAt: z.date(),
    changedByType: customPropertyChangedByTypeSchema,
    sourceFormSubmissionId: z.string().nullable(),
  })
  .register(asObjectType, {
    name: 'ClientCustomPropertyValueHistoryEntry',
    description: 'One recorded value of a client custom property',
  })

export const clientCustomPropertyValueHistoryPageSchema = z
  .object({
    entries: z.array(clientCustomPropertyValueHistoryEntrySchema),
    hasNextPage: z.boolean(),
    endCursor: z.string().nullable(),
  })
  .register(asObjectType, {
    name: 'ClientCustomPropertyValueHistoryPage',
    description: 'A page of a client custom property value history, newest first',
  })

export type ClientCustomPropertyValueHistoryArgs = z.infer<
  typeof clientCustomPropertyValueHistoryArgsSchema
>
export type ClientCustomPropertyValueHistoryEntry = z.infer<
  typeof clientCustomPropertyValueHistoryEntrySchema
>
export type ClientCustomPropertyValueHistoryPage = z.infer<
  typeof clientCustomPropertyValueHistoryPageSchema
>

export const createClientCustomPropertyValueInputSchema = z.object({
  clientId: z.string(),
  siteId: z.string(),
  siteCustomPropertyId: z.string(),
  value: z.string(),
})

export const customPropertyValueSchema = z.string()

export const updateClientCustomPropertyValueDataSchema = z.object({
  customPropertyValueId: z.string(),
  value: z.string(),
  updatedAt: z.date(),
})

export const clientCustomPropertyValuesResponseSchema = z
  .object({
    values: z.array(clientCustomPropertyValueSchema),
    withheldSiteCustomPropertyIds: z
      .array(z.string())
      .describe(
        'Properties that hold a value the calling client may not read, such as a form result hidden from clients'
      ),
    lockedSiteCustomPropertyIds: z
      .array(z.string())
      .describe(
        'Properties whose value the calling client may read but not change, such as a form result shown to clients'
      ),
  })
  .register(asObjectType, {
    name: 'ClientCustomPropertyValuesResponse',
    description: 'Response containing client custom property values',
  })

export const updateClientCustomPropertyValueResponseSchema = z
  .object({
    success: z.boolean(),
    message: z.string().optional(),
  })
  .register(asObjectType, {
    name: 'UpdateClientCustomPropertyValueResponse',
    description: 'Response for updating client custom property value',
  })

export const deleteClientCustomPropertyValuesResponseSchema = z
  .object({
    success: z.boolean(),
    message: z.string().optional(),
  })
  .register(asObjectType, {
    name: 'DeleteClientCustomPropertyValuesResponse',
    description: 'Response for deleting client custom property values',
  })

// Bulk update schemas for custom property values
export const updateClientCustomPropertyValuesInputSchema = z
  .object({
    clientId: z.string(),
    siteId: z.string(),
    values: z.array(
      z.object({
        siteCustomPropertyId: z.string(),
        value: z.string().nullable(),
      })
    ),
  })
  .register(asObjectType, {
    name: 'UpdateClientCustomPropertyValuesInput',
    description: 'Input for bulk updating client custom property values',
  })

export const updateClientCustomPropertyValuesResponseSchema = z
  .object({
    success: z.boolean(),
    message: z.string().optional(),
    values: z.array(clientCustomPropertyValueSchema).optional(),
    lockedSiteCustomPropertyIds: z
      .array(z.string())
      .optional()
      .describe(
        'Properties in the request left unchanged because the calling client may not change them, such as a form result'
      ),
  })
  .register(asObjectType, {
    name: 'UpdateClientCustomPropertyValuesResponse',
    description: 'Response for bulk updating client custom property values',
  })

// Database row schemas for query results
export const clientFormSubmissionWithAnswersRowSchema = z.object({
  formSubmissionId: z.string(),
  clientId: z.string(),
  siteId: z.string(),
  siteFormId: z.string(),
  formName: z.string(),
  assignedBy: z.string().nullish(),
  status: z.string(),
  assignedAt: z.date(),
  completedAt: z.date().nullable().optional(),
  siteFormQuestionId: z.string().nullable(),
  answer: z.string().nullable(),
  answeredAt: z.date().nullable(),
})

export const clientFormSubmissionWithClientRowSchema = z.object({
  formSubmissionId: z.string(),
  clientId: z.string(),
  siteId: z.string(),
  siteFormId: z.string(),
  assignedBy: z.string().nullish(),
  status: z.string(),
  assignedAt: z.date(),
  completedAt: z.date().nullable().optional(),
  siteFormQuestionId: z.string().nullable(),
  answer: z.string().nullable(),
  answeredAt: z.date().nullable(),
})

// Type exports for client custom property values
export type ClientCustomPropertyValueSchema = z.infer<typeof clientCustomPropertyValueSchema>
export type ClientCustomPropertyValueInputSchema = z.infer<
  typeof clientCustomPropertyValueInputSchema
>
export type ClientCustomPropertyValueRecordSchema = z.infer<
  typeof clientCustomPropertyValueRecordSchema
>
export type ClientCustomPropertyValueHistoryRecordSchema = z.infer<
  typeof clientCustomPropertyValueHistoryRecordSchema
>
export type CreateClientCustomPropertyValueInputSchema = z.infer<
  typeof createClientCustomPropertyValueInputSchema
>
export type CustomPropertyValueSchema = z.infer<typeof customPropertyValueSchema>
export type UpdateClientCustomPropertyValueDataSchema = z.infer<
  typeof updateClientCustomPropertyValueDataSchema
>
export type UpdateClientCustomPropertyValuesInputSchema = z.infer<
  typeof updateClientCustomPropertyValuesInputSchema
>
export type UpdateClientCustomPropertyValuesResponseSchema = z.infer<
  typeof updateClientCustomPropertyValuesResponseSchema
>
export type CustomPropertyWithClientValueSchema = z.infer<
  typeof customPropertyWithClientValueSchema
>

// The admin "Update user password" dialog enforces this policy client-side through the same
// constants; Firebase itself only rejects passwords shorter than 6 characters, so it has to be
// re-checked server-side. Both sides read one definition so the API can't accept a password the
// dialog would reject (whitespace, for one).
export const clientPasswordSchema = z
  .string()
  .min(PASSWORD_MIN_LENGTH, `Password must be at least ${PASSWORD_MIN_LENGTH} characters`)
  .max(PASSWORD_MAX_LENGTH, `Password must be ${PASSWORD_MAX_LENGTH} characters or less`)
  .regex(PASSWORD_UPPERCASE_REGEX, 'Password must contain an upper case character')
  .regex(PASSWORD_SPECIAL_CHARACTERS_REGEX, 'Password must contain a special character')

// Database operation schemas for client-sites
export const createClientSiteInputSchema = z.object({
  clientId: z.string().min(1),
  siteId: z.string().min(1),
  siteSpecificData: siteSpecificDataSchema.optional(),
})

export const updateClientSiteDataInputSchema = z.object({
  clientId: z.string().min(1),
  siteId: z.string().min(1),
  siteSpecificData: siteSpecificDataSchema,
})

// Database insertion schema with preprocessing
export const clientSiteInsertSchema = z.object({
  clientId: z.string(),
  siteId: z.string(),
  joinDate: z.date(),
  status: z
    .enum(Object.values(ClientSiteStatus) as [string, ...string[]])
    .default(ClientSiteStatus.Active),
  siteSpecificData: z.preprocess(
    (val) => (val == null ? null : JSON.stringify(val)),
    z.string().nullable()
  ),
  notesCount: z.number().int().default(0),
  attendanceCount: z.number().int().default(0),
  firstAttendanceDate: z.date().nullable().optional(),
  firstBookingDate: z.date().nullable().optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
  blockedAt: z.date().nullish(),
  blockedByStaffUserId: z.string().nullish(),
  blockReason: z.string().nullish(),
  blockStartDate: z.iso.date().nullish(),
  blockEndDate: z.iso.date().nullish(),
})

export const clientSiteRecordSchema = clientSiteSchema.extend({
  // Database record schema includes all fields needed for database operations
  siteSpecificData: z.string().nullable(), // JSON string in database
})

/**
 * Sentinel select value for the "Other" reason in the block dialog — when chosen the
 * dialog reveals a free-text field and submits that custom text as the block reason.
 */
export const BLOCK_REASON_OTHER = '__other__'

/**
 * Block-client dialog form (OC-100). A reason is required (chosen from the site's
 * configured blocking reasons, or "Other" with free text); the block window is
 * optional. Dates are date-only strings (yyyy-mm-dd); when both are set, the end
 * date must be on or after the start date. Error messages are i18n keys resolved
 * by the consuming component.
 */
export const blockClientFormSchema = z
  .object({
    reason: z.string().min(1, 'clients.block.reasonRequired'),
    customReason: z.string().optional(),
    startDate: z.string().optional(),
    endDate: z.string().optional(),
  })
  .refine((data) => !data.startDate || !data.endDate || data.endDate >= data.startDate, {
    path: ['endDate'],
    message: 'clients.block.endBeforeStart',
  })
  .refine(
    (data) => data.reason !== BLOCK_REASON_OTHER || (data.customReason?.trim().length ?? 0) > 0,
    { path: ['customReason'], message: 'clients.block.customReasonRequired' }
  )

export type BlockClientFormData = z.infer<typeof blockClientFormSchema>

// Type exports for database row schemas
export type ClientFormSubmissionWithAnswersRowSchema = z.infer<
  typeof clientFormSubmissionWithAnswersRowSchema
>
export type ClientFormSubmissionWithClientRowSchema = z.infer<
  typeof clientFormSubmissionWithClientRowSchema
>

// Query result schemas for client-sites database operations
export const clientSiteQueryRowSchema = z.object({
  clientId: z.string(),
  siteId: z.string(),
  joinDate: z.coerce.date(),
  siteSpecificData: siteSpecificDataSchema.nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  gatewayCustomerId: z.string().nullish(),
  lastBillingDate: z.coerce.date().nullish(),
  lastAttendanceDate: z.coerce.date().nullish(),
  lastBookingDate: z.coerce.date().nullish(),
  creditAccountBalance: z.string().nullish(), // Account credits balance (Big on backend, string on frontend)
  favoriteInstructorIds: z.array(z.string()).optional(), // Array of favorite instructor IDs
})

export const clientSitesForClientRowSchema = z.object({
  siteId: z.string(),
  joinDate: z.coerce.date(),
  siteSpecificData: siteSpecificDataSchema.nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  lastBillingDate: z.coerce.date().optional(),
  lastAttendanceDate: z.coerce.date().optional(),
  lastBookingDate: z.coerce.date().optional(),
  creditAccountBalance: z.string().nullish(), // Account credits balance (Big on backend, string on frontend)
})

export const siteClientsRowSchema = z.object({
  clientId: z.string(),
  joinDate: z.coerce.date(),
  siteSpecificData: siteSpecificDataSchema.nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

// Count query result schema
export const countQueryResultSchema = z.object({
  count: z.number(),
})

// Bulk query result schemas
export const clientSitesQueryResultSchema = z.array(clientSitesForClientRowSchema)
export const siteClientsQueryResultSchema = z.array(siteClientsRowSchema)

// Input validation for delete operations
export const removeClientSiteInputSchema = z.object({
  clientId: z.string().min(1),
  siteId: z.string().min(1),
})

export const getSiteClientCountInputSchema = z.object({
  siteId: z.string().min(1),
})

// Schema for getClientSitesWithNames repository method result
export const clientSiteWithNameRowSchema = z.object({
  siteId: z.string(),
  site: z.object({
    name: z.string(),
  }),
  joinDate: z.coerce.date().nullable(),
  checkInCount: z.number().int().default(0),
  lastCheckInOn: z.coerce.date().nullable().optional(),
})

export const clientSitesWithNamesQueryResultSchema = z.array(clientSiteWithNameRowSchema)

// Type exports for client-site schemas
export type CreateClientSiteInputSchema = z.infer<typeof createClientSiteInputSchema>
export type UpdateClientSiteDataInputSchema = z.infer<typeof updateClientSiteDataInputSchema>
export type ClientSiteRecordSchema = z.infer<typeof clientSiteRecordSchema>
export type ClientSiteQueryRowSchema = z.infer<typeof clientSiteQueryRowSchema>
export type ClientSitesForClientRowSchema = z.infer<typeof clientSitesForClientRowSchema>
export type SiteClientsRowSchema = z.infer<typeof siteClientsRowSchema>
export type CountQueryResultSchema = z.infer<typeof countQueryResultSchema>
export type ClientSitesQueryResultSchema = z.infer<typeof clientSitesQueryResultSchema>
export type SiteClientsQueryResultSchema = z.infer<typeof siteClientsQueryResultSchema>
export type RemoveClientSiteInputSchema = z.infer<typeof removeClientSiteInputSchema>
export type GetSiteClientCountInputSchema = z.infer<typeof getSiteClientCountInputSchema>
export type ClientSiteWithNameRowSchema = z.infer<typeof clientSiteWithNameRowSchema>
export type ClientSitesWithNamesQueryResultSchema = z.infer<
  typeof clientSitesWithNamesQueryResultSchema
>

// Contact Log Schema
//
// The repository derives its SELECT column list from this shape, so adding a
// key here is what makes the column readable by the contact-log screen.
export const clientContactLogSchema = z
  .object({
    communicationsLogId: z.string(),
    renderedSubject: z.string().nullable(),
    renderedContent: z.string().nullable(),
    channel: z.string(),
    sentAt: z.date().nullable(),
    createdAt: z.date(),
    // Delivery outcome. sentAt records only that the provider accepted the
    // message, and is never cleared afterwards, so it cannot distinguish a
    // delivered email from one that later bounced — read status instead (OC-813).
    //
    // Deliberately a plain string rather than communicationLogStatusSchema: these
    // values come from third-party webhooks, and a GraphQL enum throws when
    // serialising an unknown member, which would fail the entire contact-log query
    // instead of degrading. Readers narrow with CommunicationLogStatus and fall
    // back gracefully on anything unrecognised.
    status: z.string().nullable(),
    failureReason: z.string().nullable(),
    bouncedAt: z.date().nullable(),
  })
  .register(asObjectType, {
    name: 'ClientContactLog',
    description: 'A contact log entry for a client',
  })

export const clientContactLogsResponseSchema = z.array(clientContactLogSchema)

export const contactLogFiltersSchema = z
  .object({
    channel: z
      .object({
        value: z.string(),
      })
      .optional(),
    sentAt: z
      .object({
        value: z.array(z.string()).length(2),
      })
      .optional(),
  })
  .optional()

export const contactLogPaginationArgsSchema = z.object({
  clientId: z.string(),
  siteId: z.string(),
  filters: contactLogFiltersSchema,
  pageSize: z.number().optional(),
  cursor: z.string().optional(),
})

export const clientContactLogPaginationResultSchema =
  createPaginationWithCursorResultSchema(clientContactLogSchema)

export type ClientContactLogSchema = z.infer<typeof clientContactLogSchema>
export type ClientContactLogsResponseSchema = z.infer<typeof clientContactLogsResponseSchema>
export type ContactLogFiltersSchema = z.infer<typeof contactLogFiltersSchema>
export type ContactLogPaginationArgsSchema = z.infer<typeof contactLogPaginationArgsSchema>

// ============================================
// PASSWORD RESET SCHEMAS
// ============================================

// Change password input schema for frontend form validation
export const changePasswordFormSchema = z
  .object({
    passwordType: z.enum(['random', 'custom'], {
      message: 'Password type is required',
    }),
    customPassword: z.string().optional(),
    showPassword: z.boolean(),
  })
  .superRefine((data, ctx) => {
    // Custom validation for customPassword when passwordType is 'custom'
    if (data.passwordType === 'custom') {
      if (!data.customPassword || data.customPassword.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['customPassword'],
          message: 'Password cannot be empty',
        })
      } else if (data.customPassword.length < 6) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['customPassword'],
          message: 'Password must be at least 6 characters',
        })
      } else if (!/^(?=.*[0-9])(?=.*[!@#$%^&*(),.?":{}|<>])/.test(data.customPassword)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['customPassword'],
          message: 'Password must contain at least one number and one symbol',
        })
      }
    }
  })

// Password type enum
export const passwordTypeSchema = z.enum(['RANDOM', 'CUSTOM']).register(asEnumType, {
  name: 'PasswordType',
  description: 'Type of password to set',
})

// Change password input schema for GraphQL mutations
export const changePasswordInputSchema = z
  .object({
    type: passwordTypeSchema,
    customPassword: z.string().nullable().optional(),
  })
  .register(asObjectType, {
    name: 'ChangePasswordInput',
    description: 'Input for changing client password',
  })

// Change password response schema
export const changePasswordResponseSchema = z
  .object({
    success: z.boolean(),
    message: z.string(),
  })
  .register(asObjectType, {
    name: 'ChangePasswordResponse',
    description: 'Response for change password mutation',
  })

// Client permissions schema
export const clientPermissionsSchema = z
  .object({
    canChangePassword: z.boolean(),
    canEdit: z.boolean(),
    canDelete: z.boolean(),
  })
  .register(asObjectType, {
    name: 'ClientPermissions',
    description: 'Client permissions for the current user',
  })

// Schema for currently enabled filters to help frontend disable filter buttons
export const enabledFilterSchema = z
  .object({
    type: z.string(),
    label: z.string(),
    category: z.string(),
    operator: z.string(),
    displayValue: z.string(),
  })
  .register(asObjectType, {
    name: 'EnabledFilter',
    description: 'Information about currently enabled filters',
  })

export const enabledFiltersSchema = z
  .object({
    filters: z.array(enabledFilterSchema),
    totalCount: z.number(),
  })
  .register(asObjectType, {
    name: 'EnabledFilters',
    description: 'Currently enabled filters information',
  })

// Extend the base pagination result to include permissions and enabled filters
export const clientsResultSchema = clientPaginationResultSchema
  .extend({
    permissions: clientPermissionsSchema, // User permissions for all clients
    enabledFilters: enabledFiltersSchema.optional(), // Currently enabled filters
    // Whether any client across the entire base (current site/corporate scope)
    // has a barcode. The web list hides the Barcode column when this is false so
    // a base with no barcodes doesn't show a redundant empty column.
    hasAnyBarcode: z.boolean().optional(),
  })
  .register(asObjectType, {
    name: 'ClientsResult',
    description: 'Paginated clients result with user permissions and enabled filters',
  })

// OC-8: the minimal identity a MEMBER may see when picking a shared-credit recipient.
export const shareableClientSchema = z
  .object({
    id: z.string(),
    email: z.string().nullable(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
  })
  .register(asObjectType, {
    name: 'ShareableClient',
    description: 'A client a member can pick as a shared-credit recipient',
  })
export type ShareableClientSchema = z.infer<typeof shareableClientSchema>

// Individual client result with permissions
export const clientResultSchema = z
  .object({
    client: clientPublicSchema.nullable(),
    permissions: clientPermissionsSchema,
  })
  .register(asObjectType, {
    name: 'ClientResult',
    description: 'Individual client result with user permissions',
  })

// Sort options for listAll clients query
export const findClientsSortFieldsEnumSchema = z
  .enum(['clientId', 'firstName', 'lastName', 'email', 'mobilePhoneNumber', 'createdAt'])
  .register(asEnumType, {
    name: 'FindClientsSortFields',
  })

export const findClientsSortOptionsSchema = z
  .object({
    field: findClientsSortFieldsEnumSchema.optional(),
    direction: sortDirectionEnumSchema.optional(),
  })
  .register(asObjectType, {
    name: 'FindClientsSortOptions',
  })

// Client Note schemas
export const clientNoteSchema = z.object({
  noteId: z.string(),
  clientId: z.string(),
  siteId: z.string(),
  content: z.string().nullable(),
  createdBy: z.string(),
  createdByName: z.string().nullable(),
  createdAt: z.date(),
})

export const clientNotePublicSchema = clientNoteSchema
  .extend({
    createdByEmail: z.string().optional(),
  })
  .register(asObjectType, {
    name: 'ClientNote',
    description: 'A note associated with a client',
  })

export const clientNoteCreateInputSchema = z
  .object({
    clientId: z.string().min(1, 'Client ID is required'),
    content: z.string().max(10000, 'Content must be 10,000 characters or less').nullable(),
  })
  .register(asObjectType, {
    name: 'ClientNoteCreateInput',
    description: 'Input for creating a client note',
  })

export const clientNoteUpdateInputSchema = z
  .object({
    content: z
      .string()
      .max(10000, 'Content must be 10,000 characters or less')
      .nullable()
      .optional(),
  })
  .register(asObjectType, {
    name: 'ClientNoteUpdateInput',
    description: 'Input for updating a client note',
  })

export const clientNotesResponseSchema = z
  .object({
    notes: z.array(clientNotePublicSchema),
  })
  .register(asObjectType, {
    name: 'ClientNotesResponse',
    description: 'Response containing client notes',
  })

export const createClientNoteResponseSchema = z
  .object({
    note: clientNotePublicSchema.nullable(),
    success: z.boolean(),
    message: z.string().optional(),
  })
  .register(asObjectType, {
    name: 'CreateClientNoteResponse',
    description: 'Response for creating a client note',
  })

export const updateClientNoteResponseSchema = z
  .object({
    note: clientNotePublicSchema.nullable(),
    success: z.boolean(),
    message: z.string().optional(),
  })
  .register(asObjectType, {
    name: 'UpdateClientNoteResponse',
    description: 'Response for updating a client note',
  })

export const deleteClientNoteResponseSchema = z
  .object({
    success: z.boolean(),
    message: z.string().optional(),
  })
  .register(asObjectType, {
    name: 'DeleteClientNoteResponse',
    description: 'Response for deleting a client note',
  })

// Password reset confirmation dialog props schema
export const passwordResetConfirmationDialogPropsSchema = z.object({
  clientName: z.string(),
  clientEmail: z.string(),
  onConfirm: z.function(),
  onSuccess: z.function().optional(),
})

export type PasswordResetConfirmationDialogPropsSchema = z.infer<
  typeof passwordResetConfirmationDialogPropsSchema
>

export type PasswordTypeSchema = z.infer<typeof passwordTypeSchema>
export type ChangePasswordFormSchema = z.infer<typeof changePasswordFormSchema>
export type ChangePasswordInputSchema = z.infer<typeof changePasswordInputSchema>
export type ChangePasswordResponseSchema = z.infer<typeof changePasswordResponseSchema>
export type ClientPermissionsSchema = z.infer<typeof clientPermissionsSchema>
export type ClientNoteSchema = z.infer<typeof clientNoteSchema>
export type ClientNotePublicSchema = z.infer<typeof clientNotePublicSchema>
export type ClientNoteCreateInputSchema = z.infer<typeof clientNoteCreateInputSchema>
export type ClientNoteUpdateInputSchema = z.infer<typeof clientNoteUpdateInputSchema>
export type ClientNotesResponseSchema = z.infer<typeof clientNotesResponseSchema>
export type CreateClientNoteResponseSchema = z.infer<typeof createClientNoteResponseSchema>
export type UpdateClientNoteResponseSchema = z.infer<typeof updateClientNoteResponseSchema>
export type DeleteClientNoteResponseSchema = z.infer<typeof deleteClientNoteResponseSchema>
export type UpdateClientCustomPropertyValueResponseSchema = z.infer<
  typeof updateClientCustomPropertyValueResponseSchema
>
export type DeleteClientCustomPropertyValuesResponseSchema = z.infer<
  typeof deleteClientCustomPropertyValuesResponseSchema
>

export const soapNoteContentSchema = z.object({
  sessionTitle: z.string(),
  sessionDate: z.string(),
  trainerName: z.string(),
  subjective: z.string().nullable(),
  objective: z.string().nullable(),
  assessment: z.string().nullable(),
  plan: z.string().nullable(),
})

export const soapNoteSchema = z.object({
  soapNoteId: z.string(),
  sessionId: z.string(),
  clientId: z.string(),
  siteId: z.string(),
  staffUserId: z.string(),
  createdByName: z.string(),
  content: soapNoteContentSchema,
  createdAt: z.date(),
  modifiedAt: z.date().nullable(),
  modifiedBy: z.string().nullable(),
  modifiedByName: z.string().nullable(),
})

export const soapNotePublicSchema = soapNoteSchema.register(asObjectType, {
  name: 'SoapNote',
  description: 'A SOAP session note',
})

export const soapNoteCreateInputSchema = z
  .object({
    sessionId: z.string().min(1),
    clientId: z.string().min(1),
    content: soapNoteContentSchema,
  })
  .register(asObjectType, {
    name: 'SoapNoteCreateInput',
    description: 'Input for creating a SOAP note',
  })

export const soapNoteUpdateInputSchema = z
  .object({
    content: soapNoteContentSchema.optional(),
  })
  .register(asObjectType, {
    name: 'SoapNoteUpdateInput',
    description: 'Input for updating a SOAP note',
  })

export const soapNotesResponseSchema = z
  .object({
    notes: z.array(soapNotePublicSchema),
  })
  .register(asObjectType, {
    name: 'SoapNotesResponse',
    description: 'Response containing SOAP notes',
  })

export const createSoapNoteResponseSchema = z
  .object({
    note: soapNotePublicSchema.nullable(),
    success: z.boolean(),
    message: z.string().optional(),
  })
  .register(asObjectType, {
    name: 'CreateSoapNoteResponse',
    description: 'Response for creating a SOAP note',
  })

export const updateSoapNoteResponseSchema = z
  .object({
    note: soapNotePublicSchema.nullable(),
    success: z.boolean(),
    message: z.string().optional(),
  })
  .register(asObjectType, {
    name: 'UpdateSoapNoteResponse',
    description: 'Response for updating a SOAP note',
  })

export const deleteSoapNoteResponseSchema = z
  .object({
    success: z.boolean(),
    message: z.string().optional(),
  })
  .register(asObjectType, {
    name: 'DeleteSoapNoteResponse',
    description: 'Response for deleting a SOAP note',
  })

export type FindClientsSortFieldsSchema = z.infer<typeof findClientsSortFieldsEnumSchema>
export type FindClientsSortOptionsSchema = z.infer<typeof findClientsSortOptionsSchema>
export type SoapNoteContentSchema = z.infer<typeof soapNoteContentSchema>
export type SoapNoteSchema = z.infer<typeof soapNoteSchema>
export type SoapNotePublicSchema = z.infer<typeof soapNotePublicSchema>
export type SoapNoteCreateInputSchema = z.infer<typeof soapNoteCreateInputSchema>
export type SoapNoteUpdateInputSchema = z.infer<typeof soapNoteUpdateInputSchema>
export type SoapNotesResponseSchema = z.infer<typeof soapNotesResponseSchema>
export type CreateSoapNoteResponseSchema = z.infer<typeof createSoapNoteResponseSchema>
export type UpdateSoapNoteResponseSchema = z.infer<typeof updateSoapNoteResponseSchema>
export type DeleteSoapNoteResponseSchema = z.infer<typeof deleteSoapNoteResponseSchema>

// Search result client schema for corporate client lookup (not registered as GraphQL type - used internally)
export const searchResultClientSchema = z.object({
  id: z.string().uuid(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  name: z.string().optional(),
  email: z.string().email().optional(),
  mobilePhoneNumber: z.string().optional(),
  homePhoneNumber: z.string().optional(),
  dateOfBirth: z.iso.date().optional(),
  homeSiteId: z.string().uuid().optional(),
  homeSiteName: z.string().optional(),
})

export type SearchResultClientSchema = z.infer<typeof searchResultClientSchema>

// Schema for updating client communication preferences (subset of clientUpdateSchema)
export const updateClientCommunicationPreferencesInputSchema = clientUpdateSchema.pick({
  communicationPreferences: true,
})

export type UpdateClientCommunicationPreferencesInputSchema = z.infer<
  typeof updateClientCommunicationPreferencesInputSchema
>

// Schema for EditCommunicationsDialog props
export const editCommunicationsDialogPropsSchema = z.object({
  client: clientPublicSchema.pick({ id: true, communicationPreferences: true }),
  onSave: z.function(), // Validates it's a function, signature not validated
})

export type EditCommunicationsDialogPropsSchema = z.infer<
  typeof editCommunicationsDialogPropsSchema
>

// Schema for EditCustomPropertiesDialog props
export const editCustomPropertiesDialogPropsSchema = z.object({
  client: clientPublicSchema.pick({ id: true, customProperties: true }),
  onClientUpdate: z.function().optional(), // Validates it's a function if provided, signature not validated
})

export type EditCustomPropertiesDialogPropsSchema = z.infer<
  typeof editCustomPropertiesDialogPropsSchema
>

// Simplified schema for custom property with client value (used in EditCustomPropertiesDialog)
export const customPropertyWithClientValueSimplifiedSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.string(),
  options: z
    .array(
      z.object({
        id: z.string().nullable().optional(),
        option: z.string(),
        displayOrder: z.number().nullable().optional(),
      })
    )
    .nullable()
    .optional(),
  hasSubtext: z.boolean(),
  subtext: z.string().nullable().optional(),
  clientValue: z
    .object({
      customPropertyValueId: z.string(),
      value: z.string(),
      updatedAt: z.string(),
    })
    .nullable()
    .optional(),
})

export type CustomPropertyWithClientValueSimplifiedSchema = z.infer<
  typeof customPropertyWithClientValueSimplifiedSchema
>

// Schema for EditCustomPropertiesDialog form data
export const editCustomPropertiesFormDataSchema = z.record(
  z.string(),
  z.union([z.string(), z.boolean(), z.null()])
)

export type EditCustomPropertiesFormDataSchema = z.infer<typeof editCustomPropertiesFormDataSchema>

// Schema for EditEmergencyContactDialog props
export const editEmergencyContactDialogPropsSchema = z.object({
  client: clientPublicSchema.pick({ id: true, emergencyContact: true }),
  onSave: z.function(), // Validates it's a function, signature not validated
})

export type EditEmergencyContactDialogPropsSchema = z.infer<
  typeof editEmergencyContactDialogPropsSchema
>

// Schema for EditEmergencyContactDialog form data
export const editEmergencyContactFormDataSchema = z.object({
  firstName: z.string(),
  relationshipStatus: z.string(),
  phone: z.string(),
  email: z.string(),
  unit: z.string(),
  streetNumber: z.string(),
  street: z.string(),
  city: z.string(),
  state: z.string(),
  zipCode: z.string(),
  country: z.string(),
})

export type EditEmergencyContactFormDataSchema = z.infer<typeof editEmergencyContactFormDataSchema>

// Schema for updating client emergency contact (subset of clientUpdateSchema)
export const updateClientEmergencyContactInputSchema = clientUpdateSchema.pick({
  emergencyContact: true,
})

export type UpdateClientEmergencyContactInputSchema = z.infer<
  typeof updateClientEmergencyContactInputSchema
>

// Schema for Alert used in ClientInformation component
export const clientInformationAlertSchema = z.object({
  type: z.string(),
  severity: z.string(),
  message: z.string(),
  priority: z.string(),
  createdAt: z.string(),
})

export type ClientInformationAlertSchema = z.infer<typeof clientInformationAlertSchema>

// Schema for Note used in ClientInformation component
export const clientInformationNoteSchema = z.object({
  id: z.string(),
  type: z.string(),
  content: z.string(),
  createdAt: z.string(),
  author: z.object({
    id: z.string(),
    name: z.string(),
  }),
})

export type ClientInformationNoteSchema = z.infer<typeof clientInformationNoteSchema>

// Schema for SmartGroup used in ClientInformation component
export const clientInformationSmartGroupSchema = z.object({
  id: z.string(),
  name: z.string(),
  isVisible: z.boolean(),
  sortOrder: z.number(),
  staffUserId: z.string().optional(),
  filters: z.any().optional(),
})

export type ClientInformationSmartGroupSchema = z.infer<typeof clientInformationSmartGroupSchema>

// Schema for ClientInformation component Client interface (simplified frontend shape)
export const clientInformationClientSchema = clientPublicSchema
  .extend({
    // Override alerts to use simplified schema
    alerts: z.array(clientInformationAlertSchema).optional(),
    // Override notes to use simplified schema
    notes: z.array(clientInformationNoteSchema).optional(),
    // Override smartGroups to use simplified schema
    smartGroups: z.array(clientInformationSmartGroupSchema).optional(),
    // Add fields that might be missing from clientPublicSchema
    status: z.string().optional(),
    membershipType: z.string().optional(),
    barcode: z.string().optional(),
    avatarColor: z.string().optional(),
    profile: z.record(z.string(), z.any()).optional(),
    // Ensure relations and buddies are properly typed
    relations: z
      .array(
        z.object({
          clientId: z.string(),
          familyMemberClientId: z.string(),
          name: z.string(),
          email: z.string().nullable().optional(),
          relationship: z.string(),
          relationshipInverse: z.string(),
          isPrimaryAccountHolder: z.boolean(),
          createdAt: z.coerce.date(),
          updatedAt: z.coerce.date(),
        })
      )
      .optional(),
    buddies: z
      .array(
        z.object({
          id: z.string(),
          firstName: z.string().optional(),
          lastName: z.string().optional(),
          name: z.string().optional(),
          email: z.string().optional(),
          mobilePhoneNumber: z.string().optional(),
          dateOfBirth: z.string().optional(),
          homeSiteId: z.string().optional(),
          homeSiteName: z.string().optional(),
        })
      )
      .optional(),
    // Ensure siteRelationships can accept string/number dates
    siteRelationships: z
      .array(
        z.object({
          siteId: z.string(),
          joinDate: z.coerce.date(),
          siteSpecificData: z
            .object({
              enrollmentSource: z.string().optional(),
              membershipType: z.string().optional(),
              notes: z.string().optional(),
              preferences: z
                .object({
                  notifications: z.boolean().optional(),
                  communications: z.string().optional(),
                })
                .optional(),
            })
            .optional(),
          createdAt: z.coerce.date().optional(),
          updatedAt: z.coerce.date().optional(),
          lastBillingDate: z.coerce.date().optional(),
          lastAttendanceDate: z.coerce.date().optional(),
          lastBookingDate: z.coerce.date().optional(),
        })
      )
      .optional(),
  })
  .catchall(z.any()) // Allow additional fields that might come from GraphQL

export type ClientInformationClientSchema = z.infer<typeof clientInformationClientSchema>

// Schema for ClientInformation component props
export const clientInformationPropsSchema = z.object({
  client: clientInformationClientSchema,
  onClientUpdate: z.function().optional(), // Validates it's a function if provided, signature not validated
})

export type ClientInformationPropsSchema = z.infer<typeof clientInformationPropsSchema>

/**
 * Pending agreement item
 */
export const PendingAgreementSchema = z
  .object({
    siteID: z.string(),
    siteName: z.string(),
    packageID: z.string(),
    packageName: z.string(),
    packageSubscriptionID: z.string(),
    invoiceId: z.string().nullable(),
    invoiceTotal: z.number(),
  })
  .register(asObjectType, {
    name: 'PendingAgreement',
    description: 'Pending agreement requiring signature',
  })

export type PendingAgreement = z.infer<typeof PendingAgreementSchema>

/**
 * Pending policy item
 */
export const PendingPolicySchema = z
  .object({
    policyID: z.string(),
    policyName: z.string(),
  })
  .register(asObjectType, {
    name: 'PendingPolicy',
    description: 'Pending policy requiring acknowledgment',
  })

export type PendingPolicy = z.infer<typeof PendingPolicySchema>

/**
 * OC-1296: an organisation is billed and invoiced but never books, so it needs a company name
 * and a billing email and none of the person fields.
 */
export const organisationCreateSchema = z
  .object({
    companyName: z.string().trim().min(1).max(255),
    billingEmail: z.email().max(320),
    taxIdentifier: z.string().trim().max(50).optional(),
    phone: z.string().trim().max(20).optional(),
  })
  .register(asObjectType, {
    name: 'CreateOrganisationInput',
    description: 'Input for creating an organisation client',
  })

export type OrganisationCreateInput = z.infer<typeof organisationCreateSchema>

export const employeeLinkInputSchema = z
  .object({
    firstName: z.string().trim().max(50).default(''),
    lastName: z.string().trim().max(50).default(''),
    email: z.string().trim().max(320),
  })
  .register(asObjectType, {
    name: 'EmployeeLinkInput',
    description: 'One employee row to link to an organisation',
  })

export type EmployeeLinkInput = z.infer<typeof employeeLinkInputSchema>

export const EmployeeLinkOutcomeEnum = z
  .enum([
    'LINKED',
    'CREATED_AND_LINKED',
    'ALREADY_LINKED',
    'INVALID_EMAIL',
    'DUPLICATE_EMAIL',
    'FAILED',
  ])
  .register(asEnumType, { name: 'EmployeeLinkOutcome' })

export const employeeLinkResultSchema = z
  .object({
    email: z.string(),
    outcome: EmployeeLinkOutcomeEnum,
    clientId: z.string().nullable(),
    reason: z.string().nullable(),
  })
  .register(asObjectType, {
    name: 'EmployeeLinkResult',
    description: 'What happened to one employee row',
  })

export type EmployeeLinkResult = z.infer<typeof employeeLinkResultSchema>

export const organisationEmployeeSchema = z
  .object({
    clientId: z.string(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    email: z.string().nullable(),
    linkedAt: z.date().nullable(),
  })
  .register(asObjectType, {
    name: 'OrganisationEmployee',
    description: 'A client an organisation pays for',
  })

export type OrganisationEmployee = z.infer<typeof organisationEmployeeSchema>
